import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { FiSearch, FiCalendar, FiAlertCircle, FiCheckCircle, FiX, FiDatabase, FiShield, FiClock, FiFileText, FiDatabase as FiDatabaseIcon, FiPaperclip, FiRefreshCw, FiEye, FiCheckSquare } from "react-icons/fi";
import "./staffcss/medicalappointments.css";
import CancelAppointmentModal from "../../components/usercomponents/CancelAppointmentModal";
import { appointmentService } from "../../services/appointmentService";
import { auth, db } from "../../firebase";
import { doc, getDoc, onSnapshot, updateDoc, arrayRemove } from "firebase/firestore";
import { EmailAuthProvider, reauthenticateWithCredential } from "firebase/auth";
import Banner from "./Banner";
import {
  generateDynamicSlots,
  getServiceRule,
  formatDateToPH,
  getPHNow,
  getSlotValidation,
  SERVICE_FILTER_OPTIONS,
  matchesServiceFilter
} from "../../utils/appointmentUtils";
import {
  getAppointmentDateTimeValue,
  getAppointmentSortIndicator,
  getAppointmentSortTitle,
  getNextAppointmentSortConfig,
  sortAppointments
} from "../../utils/appointmentSorting";
import { isWeekend, getNextBusinessDay } from "../../utils/dateValidation";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { createPDF } from "../../utils/pdfExport";
import * as XLSX from 'xlsx';
import Portal from "../../components/Portal";

const getAssistanceBadgeColor = (reason) => {
  const colors = {
    minor: '#dbeafe',
    senior: '#fef3c7',
    pwd: '#d1fae5',
    nrnw: '#fce7f3',
    other: '#e5e7eb'
  };
  return colors[reason?.toLowerCase()] || colors.other;
};

const getAssistanceBadgeTextColor = (reason) => {
  const colors = {
    minor: '#1e40af',
    senior: '#92400e',
    pwd: '#065f46',
    nrnw: '#9d174d',
    other: '#374151'
  };
  return colors[reason?.toLowerCase()] || colors.other;
};

function NotificationModal({ message, type, onClose }) {
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        onClose();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message, onClose]);

  if (!message) return null;

  return (
    <Portal>
      <div
        className={`feedback-modal ${type}`}
        style={{
          position: "fixed",
          top: "20px",
          right: "20px",
          padding: "12px 20px",
          borderRadius: "8px",
          color: "#fff",
          backgroundColor: type === "success" ? "#36C667" : "#e74c3c",
          boxShadow: "0 4px 6px rgba(0,0,0,0.2)",
          zIndex: 10001,
          transition: "opacity 0.3s",
        }}
      >
        {message}
      </div>
    </Portal>
  );
}

const getStatusLabel = (status) => {
  const mapping = {
    'PENDING': 'Pending Approval',
    'CONFIRMED': 'Confirmed',
    'ACCEPTED': 'Accepted',
    'CANCELLING': 'User Requesting Cancellation',
    'COMPLETED': 'Completed',
    'CANCELLED_BY_USER': 'Cancelled by User',
    'CANCELLED_BY_PRO': 'Cancelled by You'
  };
  return mapping[status] || status;
};

const STATUS_FILTER_TABS = [
  { label: "All Statuses", value: "" },
  { label: "Confirmed", value: "CONFIRMED" },
  { label: "Accepted", value: "ACCEPTED" }
];

const getAttachmentExpiry = (appointment) => {
  if (!appointment) return null;

  if (appointment.editDeadline) {
    const deadline = new Date(appointment.editDeadline);
    if (!Number.isNaN(deadline.getTime())) return deadline;
  }

  const completedAt = appointment.completedAt?.toDate
    ? appointment.completedAt.toDate()
    : appointment.completedAt
      ? new Date(appointment.completedAt)
      : null;
  if (!completedAt || Number.isNaN(completedAt.getTime())) return null;

  // Legacy completed appointments use the same seven-day fallback as the edit guard.
  return new Date(completedAt.getTime() + (7 * 24 * 60 * 60 * 1000));
};

// ---------------- Export Modal ----------------
function ExportModal({ isOpen, onClose, onExport, isExporting }) {
  const [selectedFormat, setSelectedFormat] = React.useState(null);

  React.useEffect(() => {
    const scrollContainer = document.querySelector('.mp-workspace');
    if (!isOpen) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previousContainerOverflow = scrollContainer?.style.overflow ?? '';

    document.body.style.overflow = 'hidden';
    if (scrollContainer) {
      scrollContainer.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      if (scrollContainer) {
        scrollContainer.style.overflow = previousContainerOverflow;
      }
    };
  }, [isOpen]);

  React.useEffect(() => {
    if (isOpen) {
      setSelectedFormat(null);
    }
  }, [isOpen]);

  const handleExport = (format) => {
    setSelectedFormat(format);
    onExport(format);
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <div className="modal-overlay export-modal-overlay">
        <div className="modal-content export-modal-content">
          <div className="modal-header">
            <h3 className="modal-title-white">Export Report</h3>
            <button className="modal-close" onClick={onClose}>
              <FiX />
            </button>
          </div>

          <div className="export-modal-body">
            <p className="export-modal-subtitle">Select export format</p>

            <div className="export-format-list">
              <button
                className={`export-format-btn ${selectedFormat === 'pdf' ? 'selected' : ''}`}
                onClick={() => handleExport('pdf')}
                disabled={isExporting}
              >
                <div className="export-format-icon-wrap pdf">
                  <FiFileText className="export-format-icon" />
                </div>
                <div className="export-format-info">
                  <span className="export-format-title">PDF Report</span>
                  <span className="export-format-desc">Presentation-ready report</span>
                </div>
              </button>

              <button
                className={`export-format-btn ${selectedFormat === 'excel' ? 'selected' : ''}`}
                onClick={() => handleExport('excel')}
                disabled={isExporting}
              >
                <div className="export-format-icon-wrap excel">
                  <FiFileText className="export-format-icon" />
                </div>
                <div className="export-format-info">
                  <span className="export-format-title">Excel .xlsx</span>
                  <span className="export-format-desc">Structured analytics workbook</span>
                </div>
              </button>

              <button
                className={`export-format-btn ${selectedFormat === 'csv' ? 'selected' : ''}`}
                onClick={() => handleExport('csv')}
                disabled={isExporting}
              >
                <div className="export-format-icon-wrap csv">
                  <FiFileText className="export-format-icon" />
                </div>
                <div className="export-format-info">
                  <span className="export-format-title">CSV .csv</span>
                  <span className="export-format-desc">Raw structured data</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}

const UpcomingAppointments = ({ appointments, onComplete, onApprove, onAccept, refreshAppointments, staffProfile, sidebarCollapsed }) => {
  const [search, setSearch] = useState("");
  const [serviceFilter, setServiceFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState(null);
  const itemsPerPage = 5;

  const [viewing, setViewing] = useState(null);
  const [accessState, setAccessState] = useState("idle"); // idle | requesting | approved | viewingData
  const [showAccessRequestModal, setShowAccessRequestModal] = useState(false);
  const [showAttachmentsModal, setShowAttachmentsModal] = useState(false);
  const [showAttachmentsViewerModal, setShowAttachmentsViewerModal] = useState(false);

  const [accessDuration, setAccessDuration] = useState("");
  const [staffId, setStaffId] = useState("");
  const [staffPassword, setStaffPassword] = useState("");
  const [bannerMessage, setBannerMessage] = useState("");
  const [bannerType, setBannerType] = useState("error");
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationType, setNotificationType] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);

  // Reschedule/Cancel state (sharing from ReviewAppointments logic)
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [selectedRescheduleDate, setSelectedRescheduleDate] = useState(getNextBusinessDay(new Date()));
  const [selectedRescheduleTime, setSelectedRescheduleTime] = useState("");
  const [rescheduleReason, setRescheduleReason] = useState("schedule_conflict");
  const [rescheduleReasonOther, setRescheduleReasonOther] = useState("");
  const [rescheduleReasonError, setRescheduleReasonError] = useState("");
  const [bookedSlots, setBookedSlots] = useState([]);
  const [isSubmittingReschedule, setIsSubmittingReschedule] = useState(false);

  const [cancelTarget, setCancelTarget] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [selectedExportFormat, setSelectedExportFormat] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  // Enhanced View Data / Patient Records State
  const [showViewDataModal, setShowViewDataModal] = useState(false);
  const [patientInfo, setPatientInfo] = useState(null);
  const [patientRecords, setPatientRecords] = useState([]);
  const [isLoadingRecords, setIsLoadingRecords] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const patientAppointmentCache = useRef(new Map());

  // Attachments State
  const [clinicalNote, setClinicalNote] = useState("");
  const [attachmentFiles, setAttachmentFiles] = useState([]); // Changed to array
  const [isUploading, setIsUploading] = useState(false);
  const [noteError, setNoteError] = useState("");
  const [existingNotes, setExistingNotes] = useState([]); // Added to track existing notes
  const [selectedNoteId, setSelectedNoteId] = useState("");
  const [showAttachmentEditor, setShowAttachmentEditor] = useState(false);
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [ndaAgreed, setNdaAgreed] = useState(false);
  
  // Memoization: Track last fetched appointment to avoid redundant getDoc calls
  const lastFetchedAppointmentId = useRef(null);
  const appointmentDataCache = useRef({});

  // Tooltip state
  const [tooltip, setTooltip] = useState({ visible: false, text: '', x: 0, y: 0, type: 'default' });

  // Tooltip handlers
  const handleActionMouseEnter = (e, text, type = 'default') => {
    setTooltip({
      visible: true,
      text: text,
      x: e.clientX - 150,
      y: e.clientY,
      type: type
    });
  };

  const handleActionMouseMove = (e) => {
    if (tooltip.visible) {
      setTooltip(prev => ({
        ...prev,
        x: e.clientX - 150,
        y: e.clientY
      }));
    }
  };

  const handleActionMouseLeave = () => {
    setTooltip({ visible: false, text: '', x: 0, y: 0, type: 'default' });
  };

  // Fetch appointment data when attachments modal or view data modal opens to ensure we have latest notes/files
  useEffect(() => {
    const fetchLatestAppointmentData = async () => {
      if ((showAttachmentsModal || showAttachmentsViewerModal || showViewDataModal) && viewing?.id) {
        // Skip if we already fetched this appointment recently
        if (lastFetchedAppointmentId.current === viewing.id && appointmentDataCache.current[viewing.id]) {
          const cachedData = appointmentDataCache.current[viewing.id];
          setExistingNotes(cachedData.attachmentNotes || []);
          setViewing(prev => ({
            ...prev,
            attachments: cachedData.attachments || [],
            attachmentNotes: cachedData.attachmentNotes || []
          }));
          return;
        }

        try {
          const apptDoc = await getDoc(doc(db, "appointments", viewing.id));
          if (apptDoc.exists()) {
            const data = apptDoc.data();
            // Cache the data
            appointmentDataCache.current[viewing.id] = data;
            lastFetchedAppointmentId.current = viewing.id;
            
            setExistingNotes(data.attachmentNotes || []);
            // Update the viewing object with latest attachments too
            setViewing(prev => ({
              ...prev,
              attachments: data.attachments || [],
              attachmentNotes: data.attachmentNotes || []
            }));
          }
        } catch (err) {
          console.error("Error fetching latest appointment data:", err);
        }
      }
    };
    fetchLatestAppointmentData();
  }, [showAttachmentsModal, showAttachmentsViewerModal, showViewDataModal, viewing?.id]);

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const [appointmentsWithAssistance, setAppointmentsWithAssistance] = useState([]);

  // Fetch user assistance data for appointments
  useEffect(() => {
    const fetchAssistanceData = async () => {
      if (!appointments || appointments.length === 0) return;

      const appointmentsWithAssistance = await Promise.all(
        appointments.map(async (apt) => {
          try {
            const userRef = doc(db, "users", apt.uid);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
              const userData = userSnap.data();
              return {
                ...apt,
                assistanceRequired: userData.assistance?.assistanceRequired || userData.assistanceRequired || "",
                reasonForAssistance: userData.assistance?.reasonForAssistance || []
              };
            }
            return apt;
          } catch (err) {
            console.error("Error fetching assistance data:", err);
            return apt;
          }
        })
      );

      // Update the appointments prop is not possible, so we'll use a local state
      setAppointmentsWithAssistance(appointmentsWithAssistance);
    };

    fetchAssistanceData();
  }, [appointments]);

  // Fetch Patient Records (Medical History & Clinical History)
  useEffect(() => {
    let unsubscribe = () => {};

    const fetchPatientData = async () => {
      if ((showViewDataModal || showAttachmentsViewerModal) && viewing?.uid) {
        setIsLoadingRecords(true);
        try {
          // 1. Fetch User Document for Medical History
          const userRef = doc(db, "users", viewing.uid);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            const userData = userSnap.data();
            const isAssistedAccount = `${userData.assistanceRequired || userData.assistance?.assistanceRequired || ""}`.toLowerCase() === "yes";
            const guardianDetails = userData.guardianContactDetails || null;
            const hasGuardianDetails = Boolean(
              guardianDetails && (
                guardianDetails.guardianFullName ||
                guardianDetails.guardianRelationshipToUser ||
                guardianDetails.guardianContactNumber ||
                guardianDetails.guardianAddress
              )
            );
            const locationParts = [
              userData.contactDetails?.barangay,
              userData.contactDetails?.province,
              userData.contactDetails?.region
            ].filter(Boolean);
            const formattedLocation = locationParts.length > 0
              ? locationParts.join(", ")
              : userData.personalInformation?.province || "";

            setPatientInfo({
              healthInformation: {
                medicalHistory: userData.medicalHistory || [],
                medicalOther: userData.medicalOther || "",
                pastSurgeries: userData.pastSurgeries || [],
                surgeryOther: userData.surgeryOther || "",
                allergies: userData.allergies || [],
                allergyOther: userData.allergyOther || "",
                smokingStatus: userData.smoking || "",
                alcoholUse: userData.alcoholUse || ""
              },
              medicationHistory: {
                currentMedications: userData.currentMedications || "",
                hormonalMedications: userData.hormonalMedications || "",
                otherContraception: userData.contraceptionOther || ""
              },
              sexualHistory: {
                stiHistory: userData.stiHistory || [],
                stiOther: userData.stiOther || "",
                hivStatus: userData.hivStatus || "",
                hivDetails: userData.hivOther || "",
                lastHivTest: userData.lastTestedDate || ""
              },
              patientInformation: {
                fullName: ((userData.personalInformation?.firstName || "") + " " + (userData.personalInformation?.lastName || "")).trim(),
                uic: userData.uniqueIdentifierCode || userData.accountSetup?.uniqueIdentifierCode || "Not Provided",
                dateOfBirth: userData.personalInformation?.dob || "",
                sex: userData.personalInformation?.sex || "",
                contactNumber: (
                  userData.personalInformation?.phone ||
                  userData.phone ||
                  userData.contact ||
                  userData.contactDetails?.contact
                ) || "Not Provided",
                location: formattedLocation,
                email: userData.email || ""
              },
              assistanceRequired: userData.assistance?.assistanceRequired || userData.assistanceRequired || "",
              reasonForAssistance: userData.assistance?.reasonForAssistance || [],
              ...(isAssistedAccount && hasGuardianDetails && {
                guardianInformation: {
                  guardianFullName: guardianDetails.guardianFullName || "",
                  relationship: guardianDetails.guardianRelationshipToUser || "",
                  contactNumber: guardianDetails.guardianContactNumber || "",
                  guardianAddress: guardianDetails.guardianAddress || ""
                }
              })
            });
          }

          // 2. Subscribe to Clinical History (Shared Notes from other appointments)
          unsubscribe = appointmentService.subscribeToUserAppointments(viewing.uid, async (allAppts) => {
            try {
              const parentAppointmentId = viewing.isFollowUp === true
                ? viewing.originalAppointmentId
                : viewing.id;
              const relatedAppointments = [...allAppts];
              const appointmentIds = new Set(relatedAppointments.map(appointment => appointment.id));

              if (parentAppointmentId && !appointmentIds.has(parentAppointmentId)) {
                let parentAppointment = patientAppointmentCache.current.get(parentAppointmentId);
                if (!parentAppointment) {
                  const parentSnapshot = await getDoc(doc(db, "appointments", parentAppointmentId));
                  if (parentSnapshot.exists()) {
                    const parentData = parentSnapshot.data();
                    if (parentData.uid === viewing.uid) {
                      parentAppointment = { id: parentSnapshot.id, ...parentData };
                      patientAppointmentCache.current.set(parentAppointmentId, parentAppointment);
                    }
                  }
                }
                if (parentAppointment) relatedAppointments.push(parentAppointment);
              }

              const recordsWithNotes = relatedAppointments
                .filter(a =>
                  a.status === 'COMPLETED' ||
                  a.id === viewing.id ||
                  a.id === parentAppointmentId ||
                  (a.isFollowUp === true && a.originalAppointmentId === parentAppointmentId)
                )
                .map(a => ({
                  ...a,
                  category: a.serviceId === 'sti' ? 'STI / Sexual Health Tests' : 
                            a.serviceId === 'family' ? 'Screening / Preventive Checks' : 'Lab Tests',
                  attachments: a.attachments || [],
                  displayNotes: (() => {
                    const storedNotes = [
                      ...(a.notes || []),
                      ...(a.attachmentNotes || [])
                    ];
                    const rawNotes = storedNotes.length > 0 ? storedNotes : (a.clinicalNotes || []);
                    const normalized = rawNotes.map(n => ({
                      id: n.noteId || n.id,
                      text: n.content || n.text,
                      shared: n.visibility === "shared" || n.shared === true,
                      createdAt: n.createdAt,
                      proName: n.proName || a.proName
                    }));
                    return normalized;
                  })()
                }))
                .filter(a =>
                  a.displayNotes.length > 0 ||
                  a.attachments.length > 0 ||
                  a.id === viewing.id ||
                  a.id === parentAppointmentId
                );
                
              setPatientRecords(recordsWithNotes);
            } catch (err) {
              console.error("Error processing patient records:", err);
            } finally {
              setIsLoadingRecords(false);
            }
          });
        } catch (err) {
          console.error("Error fetching patient data:", err);
          setIsLoadingRecords(false);
        }
      } else {
        setPatientRecords([]);
        setPatientInfo(null);
      }
    };

    fetchPatientData();
    return () => unsubscribe();
  }, [
    showViewDataModal,
    showAttachmentsViewerModal,
    viewing?.uid,
    viewing?.id,
    viewing?.isFollowUp,
    viewing?.originalAppointmentId
  ]);

  const renderInfoSection = (title, data) => {
    if (!data || Object.keys(data).length === 0) return null;

    return (
      <div className="ma-patient-summary" style={{ marginBottom: '24px' }}>
        <h4 style={{ fontSize: '14px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px', fontWeight: '700' }}>{title}</h4>
        <div className="ma-modal-details" style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
          {Object.entries(data).map(([key, value]) => {
            if (!value || (Array.isArray(value) && value.length === 0)) return null;
            
            const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
            
            return (
              <div key={key} className="ma-detail-row" style={{ padding: '8px 0', borderBottom: '1px solid rgba(226, 232, 240, 0.5)' }}>
                <span className="ma-detail-label" style={{ color: '#64748b', fontSize: '13px' }}>{label}</span>
                <span className="ma-detail-value" style={{ color: '#1e293b', fontWeight: '500', fontSize: '13px', display: 'flex', justifyContent: 'flex-end', textAlign: 'right' }}>
                  {Array.isArray(value) ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'flex-end' }}>
                      {value.map((item, idx) => (
                        <span key={idx} style={{ padding: '2px 8px', background: '#eff6ff', color: '#1d4ed8', borderRadius: '4px', fontSize: '11px' }}>{item}</span>
                      ))}
                    </div>
                  ) : value}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const isAppointmentEditable = (appt) => {
    if (!appt) return false;

    // Check blockchain status first - if read_only or verified, immediately not editable
    if (appt.blockchain?.status === 'read_only' || appt.blockchain?.status === 'verified') {
      return false;
    }

    // Upcoming appointments (not completed) are always editable
    // Expiration only applies to completed appointments
    return true;
  };

  const handleAttachmentUpload = async () => {
    // Feature 4: Support saving ONLY note if no files are selected, 
    // but validate if either is present
    if (!clinicalNote.trim() && attachmentFiles.length === 0) {
      setNoteError("Please enter a note or select at least one file.");
      return;
    }

    if (clinicalNote.trim() && clinicalNote.trim().length < 10) {
      setNoteError("Notes must be at least 10 characters long.");
      return;
    }
    if (clinicalNote.length > 500) {
      setNoteError("Notes must not exceed 500 characters.");
      return;
    }

    setIsUploading(true);
    setNoteError("");
    try {
      const sanitizedNote = clinicalNote.trim().replace(/<[^>]*>?/gm, '');
      const uploadedBy = staffProfile?.fullName || staffProfile?.name || "Medical Professional";

      // Use updateAttachmentContent when editing (selectedNoteId is set)
      if (selectedNoteId) {
        await appointmentService.updateAttachmentContent(
          viewing.id,
          attachmentFiles,
          sanitizedNote,
          uploadedBy,
          viewing.attachments || [],
          removedAttachmentIds,
          selectedNoteId || null
        );
      } else {
        // Feature 4: Pass array of files for new uploads
        await appointmentService.uploadAttachment(viewing.id, attachmentFiles, sanitizedNote, uploadedBy);
      }

      setNotificationMessage("Clinical note and attachments saved successfully!");
      setNotificationType("success");
      setClinicalNote("");
      setAttachmentFiles([]);
      setRemovedAttachmentIds([]);
      setSelectedNoteId("");
      setShowAttachmentEditor(false);

      // Refresh existing notes and files by updating cache instead of re-fetching
      // The uploadAttachment service should return the updated data, but we'll invalidate cache
      delete appointmentDataCache.current[viewing.id];
      lastFetchedAppointmentId.current = null;

      // Fetch fresh data once to update UI
      const apptDoc = await getDoc(doc(db, "appointments", viewing.id));
      if (apptDoc.exists()) {
        const data = apptDoc.data();
        appointmentDataCache.current[viewing.id] = data;
        lastFetchedAppointmentId.current = viewing.id;
        setExistingNotes(data.attachmentNotes || []);
        setViewing(prev => ({
          ...prev,
          attachments: data.attachments || [],
          attachmentNotes: data.attachmentNotes || []
        }));
      }
    } catch (err) {
      console.error("Error uploading attachment:", err);
      setBannerMessage("Failed to upload. " + err.message);
      setBannerType("error");
    } finally {
      setIsUploading(false);
    }
  };

  // Dynamic Time Slots
  const [timeSlots, setTimeSlots] = useState({ morning: [], afternoon: [] });

  useEffect(() => {
    if (!rescheduleTarget) return;
    // Prevent time slot generation on weekends
    if (isWeekend(selectedRescheduleDate)) {
      setTimeSlots({ morning: [], afternoon: [] });
      setSelectedRescheduleTime("");
      return;
    }
    const slots = generateDynamicSlots(rescheduleTarget.serviceId || rescheduleTarget.serviceName);
    setTimeSlots(slots);
    setSelectedRescheduleTime("");
  }, [rescheduleTarget, selectedRescheduleDate]);

  useEffect(() => {
    const fetchBookedSlots = async () => {
      if (!rescheduleTarget || !selectedRescheduleDate) return;
      try {
        const dateStr = formatDateToPH(selectedRescheduleDate);
        const slots = await appointmentService.getBookedSlots(dateStr);
        setBookedSlots(slots);
      } catch (err) {
        console.error("Error fetching booked slots:", err);
      }
    };
    fetchBookedSlots();
  }, [selectedRescheduleDate, rescheduleTarget]);

  // Ensure selectedRescheduleDate is never a weekend (safety net)
  useEffect(() => {
    if (rescheduleTarget && isWeekend(selectedRescheduleDate)) {
      const nextBusinessDay = getNextBusinessDay(selectedRescheduleDate);
      setSelectedRescheduleDate(nextBusinessDay);
      setBannerMessage("The clinic is closed on weekends. Automatically selected the next available business day.");
      setBannerType("error");
    }
  }, [selectedRescheduleDate, rescheduleTarget]);

  const isTimeSlotValid = (time) => {
    if (!rescheduleTarget) return { valid: false };
    
    // Prevent time slot selection on weekends
    if (isWeekend(selectedRescheduleDate)) {
      return { valid: false, reason: 'weekend' };
    }
    
    const rule = getServiceRule(rescheduleTarget.serviceId || rescheduleTarget.serviceName);
    const bookingType = rescheduleTarget.bookingType || (rescheduleTarget.type === "walk-in" ? "WALK_IN" : "ONLINE");

    const conflict = bookedSlots.some(slot => {
      if (slot.time !== time) return false;
      
      const sameService = slot.serviceId === rescheduleTarget.serviceId || slot.serviceName === rescheduleTarget.serviceName;
      if (!sameService) return false;

      if (rule && rule.separateBookingType === false) {
        return true; // 1 TOTAL
      } else {
        return slot.bookingType === bookingType; // 1 ONLINE + 1 WALK_IN
      }
    });

    if (conflict) return { valid: false, reason: 'Already booked' };

    const slotValidation = getSlotValidation(time, rescheduleTarget.serviceId || rescheduleTarget.serviceName);
    if (!slotValidation.valid) {
      return { valid: false, reason: slotValidation.reason === 'lunch' ? 'lunch' : 'service' };
    }

    const phNow = getPHNow();
    const [timeStr, period] = time.split(' ');
    let [hours, minutes] = timeStr.split(':').map(Number);
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;

    const selectedDateStr = formatDateToPH(selectedRescheduleDate);
    const todayPHStr = formatDateToPH(new Date());

    const isToday = selectedDateStr === todayPHStr;
    const isPastDate = selectedDateStr < todayPHStr;

    if (isPastDate) return { valid: false, reason: "past" };
    if (isToday) {
      if (hours < phNow.hour) return { valid: false, reason: "past" };
      if (hours === phNow.hour && minutes <= phNow.minute) return { valid: false, reason: "past" };
    }
    return { valid: true };
  };

  const filteredAppointments = appointmentsWithAssistance.length > 0 ? appointmentsWithAssistance.filter((apt) => {
    const searchLower = search.toLowerCase();
    const matchesSearch =
      (apt.patientName?.toLowerCase().includes(searchLower)) ||
      (apt.clinicName?.toLowerCase().includes(searchLower)) ||
      (apt.serviceName?.toLowerCase().includes(searchLower)) ||
      (apt.reference?.toLowerCase().includes(searchLower));

    const matchesService = matchesServiceFilter(apt, serviceFilter);

    let matchesDate = true;
    if (dateFrom || dateTo) {
      const aptDate = new Date(apt.date);
      if (dateFrom) {
        const fromDate = new Date(dateFrom);
        matchesDate = matchesDate && aptDate >= fromDate;
      }
      if (dateTo) {
        const toDate = new Date(dateTo);
        matchesDate = matchesDate && aptDate <= toDate;
      }
    }

    const matchesStatus = statusFilter ? apt.status === statusFilter : true;

    return matchesSearch && matchesService && matchesDate && matchesStatus;
  }) : appointments.filter((apt) => {
    const searchLower = search.toLowerCase();
    const matchesSearch =
      (apt.patientName?.toLowerCase().includes(searchLower)) ||
      (apt.clinicName?.toLowerCase().includes(searchLower)) ||
      (apt.serviceName?.toLowerCase().includes(searchLower)) ||
      (apt.reference?.toLowerCase().includes(searchLower));

    const matchesService = matchesServiceFilter(apt, serviceFilter);

    let matchesDate = true;
    if (dateFrom || dateTo) {
      const aptDate = new Date(apt.date);
      if (dateFrom) {
        const fromDate = new Date(dateFrom);
        matchesDate = matchesDate && aptDate >= fromDate;
      }
      if (dateTo) {
        const toDate = new Date(dateTo);
        matchesDate = matchesDate && aptDate <= toDate;
      }
    }

    const matchesStatus = statusFilter ? apt.status === statusFilter : true;

    return matchesSearch && matchesService && matchesDate && matchesStatus;
  });

  const getStatusTabCount = (statusValue) => {
    const searchLower = search.toLowerCase();
    const appointmentsToUse = appointmentsWithAssistance.length > 0 ? appointmentsWithAssistance : appointments;
    return appointmentsToUse.filter((apt) => {
      const matchesSearch =
        (apt.patientName?.toLowerCase().includes(searchLower)) ||
        (apt.clinicName?.toLowerCase().includes(searchLower)) ||
        (apt.serviceName?.toLowerCase().includes(searchLower)) ||
        (apt.reference?.toLowerCase().includes(searchLower));

      const matchesService = matchesServiceFilter(apt, serviceFilter);
      
      let matchesDate = true;
      if (dateFrom || dateTo) {
        const aptDate = new Date(apt.date);
        if (dateFrom) {
          const fromDate = new Date(dateFrom);
          matchesDate = matchesDate && aptDate >= fromDate;
        }
        if (dateTo) {
          const toDate = new Date(dateTo);
          matchesDate = matchesDate && aptDate <= toDate;
        }
      }
      const matchesStatus = statusValue ? apt.status === statusValue : true;

      return matchesSearch && matchesService && matchesDate && matchesStatus;
    }).length;
  };

  const handleReschedule = async () => {
    if (!rescheduleTarget || !selectedRescheduleDate || !selectedRescheduleTime) return;

    if (isWeekend(selectedRescheduleDate)) {
      setBannerMessage("The clinic is closed on weekends.\nPlease select an available weekday.");
      setBannerType("error");
      return;
    }

    const finalReason = rescheduleReason === "other" ? rescheduleReasonOther : 
      rescheduleReason === "schedule_conflict" ? "Schedule conflict with another confirmed appointment" :
      rescheduleReason === "staff_unavailable" ? "Medical staff temporarily unavailable" :
      rescheduleReason === "schedule_passed" ? "Selected appointment schedule has already passed" :
      rescheduleReason === "clinic_adjustment" ? "Clinic operational adjustment" : rescheduleReason;

    if (!finalReason || !finalReason.trim()) {
      setRescheduleReasonError("Please provide a reason for rescheduling.");
      return;
    }

    const trimmedReason = finalReason.trim();
    if (trimmedReason.length < 10) {
      setRescheduleReasonError("Reason must be at least 10 characters long.");
      return;
    }
    if (trimmedReason.length > 500) {
      setRescheduleReasonError("Reason must not exceed 500 characters.");
      return;
    }

    setRescheduleReasonError("");
    setIsSubmittingReschedule(true);
    try {
      const dateStr = formatDateToPH(selectedRescheduleDate);
      await appointmentService.rescheduleAppointment(
        rescheduleTarget.id,
        dateStr,
        selectedRescheduleTime,
        trimmedReason,
        staffProfile
      );
      setBannerMessage("Appointment rescheduled successfully.");
      setBannerType("success");
      setRescheduleTarget(null);
      setSelectedRescheduleTime("");
      setRescheduleReason("schedule_conflict");
      setRescheduleReasonOther("");
      
      // Refresh list if applicable
      if (typeof refreshAppointments === 'function') {
        refreshAppointments();
      }
    } catch (err) {
      setBannerMessage(err.message || "Failed to reschedule appointment.");
      setBannerType("error");
    } finally {
      setIsSubmittingReschedule(false);
    }
  };

  const handleCancel = async (apt, finalReason) => {
    if (!apt) return;

    setIsCancelling(true);
    try {
      const currentUser = auth.currentUser;
      await appointmentService.updateStatus(apt.id, 'CANCELLED_BY_PRO', {
        cancelReason: finalReason,
        auditCancelledId: currentUser?.uid || null,
        cancelledByRole: 'the Medical Professional',
        cancelledByEmail: currentUser?.email || ''
      });
      setBannerMessage("Appointment cancelled successfully.");
      setBannerType("success");
      setCancelTarget(null);
    } catch (err) {
      setBannerMessage("Failed to cancel appointment.");
      setBannerType("error");
    } finally {
      setIsCancelling(false);
    }
  };

  // Pagination logic
  // Pagination helpers and filter handler
  const handleFilterChange = () => {
    setCurrentPage(1);
  };

  // Set loading to false when appointments are loaded (even if empty)
  useEffect(() => {
    setLoading(false);
  }, [appointments]);

  const sortedAppointments = sortAppointments(filteredAppointments, sortConfig, (appointment, key) => {
    switch (key) {
      case "upcomingStatus":
        return appointment.status === "CONFIRMED"
          ? 0
          : appointment.status === "ACCEPTED" ? 1 : null;
      case "patientName":
        return appointment.patientName || null;
      case "reference":
        return appointment.reference || null;
      case "clinicName":
        return appointment.clinicName || null;
      case "serviceName":
        return appointment.serviceName || null;
      case "dateTime":
        return getAppointmentDateTimeValue(appointment);
      default:
        return null;
    }
  });

  const totalPages = Math.ceil(sortedAppointments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedAppointments = sortedAppointments.slice(startIndex, endIndex);

  const handlePrevPage = () => currentPage > 1 && setCurrentPage(prev => prev - 1);
  const handleNextPage = () => currentPage < totalPages && setCurrentPage(prev => prev + 1);

  const handleSort = (key) => {
    setSortConfig((current) => getNextAppointmentSortConfig(current, key));
    setCurrentPage(1);
  };

  const getSortHeaderProps = (key, label) => ({
    onClick: () => handleSort(key),
    onKeyDown: (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleSort(key);
      }
    },
    tabIndex: 0,
    title: getAppointmentSortTitle(label, key, sortConfig),
    "aria-label": getAppointmentSortTitle(label, key, sortConfig),
    "aria-sort": sortConfig?.key !== key
      ? "none"
      : sortConfig.direction === "asc" ? "ascending" : "descending",
    style: { cursor: "pointer" }
  });

  const resetAccessFlow = () => {
    setAccessState("idle");
    setAccessDuration("");
    setStaffId("");
    setStaffPassword("");
    setShowAccessRequestModal(false);
    setShowAttachmentsModal(false);
    setShowAttachmentsViewerModal(false);
  };

  const handleRefreshAppointments = async () => {
    if (typeof refreshAppointments !== 'function') return;
    setIsRefreshing(true);
    try {
      await refreshAppointments();
    } catch (err) {
      console.error("Error refreshing upcoming appointments:", err);
      setBannerMessage("Failed to refresh appointments.");
      setBannerType("error");
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSendAccessRequest = async (e) => {
    if (e) e.preventDefault();
    if (!staffId || !staffPassword || !accessDuration) {
      setBannerMessage("Please fill in all fields.");
      setBannerType("error");
      return;
    }
    
    setIsSubmitting(true);
    setBannerMessage("");
    setAccessState("requesting");

    try {
      const user = auth.currentUser;
      if (!user) throw new Error("No authenticated user");

      // STEP 1: Validate Staff ID
      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.data();

      if (userData?.staffId !== staffId) {
        setBannerMessage("The provided Staff ID does not match your account.");
        setBannerType("error");
        setIsSubmitting(false);
        setAccessState("idle");
        return;
      }

      // STEP 2: Validate Password matching backend
      try {
        const credential = EmailAuthProvider.credential(user.email, staffPassword);
        await reauthenticateWithCredential(user, credential);
      } catch (pwErr) {
        console.error("Password validation failed:", pwErr);
        setBannerMessage("Incorrect password. Please try again.");
        setBannerType("error");
        setIsSubmitting(false);
        setAccessState("idle");
        return;
      }

      // STEP 3: Save Request
      await appointmentService.requestDataAccess(
        viewing.id,
        user.uid,
        parseInt(accessDuration),
        staffProfile?.staffId,
        staffProfile
      );

      setNotificationMessage("Access request sent successfully!");
      setNotificationType("success");
      setAccessState("approved"); // Or pending approval, but based on AppointmentHistory logic it's "sent"

      setTimeout(() => {
        setShowAccessRequestModal(false);
        setAccessDuration("");
        setStaffId("");
        setStaffPassword("");
        setAccessState("idle"); // Reset for next time or stay "approved" if needed
      }, 2000);

    } catch (err) {
      console.error("Error requesting access:", err);
      setBannerMessage("Failed to send access request: " + err.message);
      setBannerType("error");
      setAccessState("idle");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenAccessedData = () => {
    setAccessState("viewingData");
  };

  const handleTerminateAccess = () => {
    setAccessState("idle");
    setAccessDuration("");
    setStaffId("");
    setStaffPassword("");
  };

  const getCalendarDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startOffset = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    const totalDays = lastDay.getDate();
    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let day = 1; day <= totalDays; day++) cells.push(day);
    return cells;
  };

  const exportToPDF = async () => {
    const pdf = createPDF({
      title: "Upcoming Appointments Report"
    });

    let startY = pdf.contentStartY;

    // Add summary
    pdf.doc.setFontSize(10);
    pdf.doc.text(`Total Appointments: ${filteredAppointments.length}`, pdf.margin, startY);
    startY += 15;

    // Separate data by status
    const confirmedItems = filteredAppointments.filter(item => item.status === 'CONFIRMED');
    const acceptedItems = filteredAppointments.filter(item => item.status === 'ACCEPTED');
    const cancellingItems = filteredAppointments.filter(item => item.status === 'CANCELLING');

    // Add confirmed items table if any
    if (confirmedItems.length > 0) {
      pdf.doc.setFontSize(14);
      pdf.doc.setFont('helvetica', 'bold');
      pdf.doc.text('CONFIRMED', pdf.margin, startY);
      startY += 10;
      
      const headers = ['Status', 'Patient', 'Reference #', 'Clinic', 'Service', 'Date & Time'];
      const rows = confirmedItems.map(apt => [
        getStatusLabel(apt.status),
        apt.patientName || 'N/A',
        apt.reference || 'N/A',
        apt.clinicName || 'N/A',
        apt.serviceName || 'N/A',
        `${apt.date || 'N/A'} ${apt.time || 'N/A'}`
      ]);

      pdf.autoTable({
        head: [headers],
        body: rows,
        startY: startY,
        margin: { top: pdf.contentStartY, bottom: pdf.footerHeight + 10, left: pdf.margin, right: pdf.margin }
      });
      startY = pdf.doc.lastAutoTable.finalY + 20;
    }

    // Add accepted items table if any
    if (acceptedItems.length > 0) {
      pdf.doc.setFontSize(14);
      pdf.doc.setFont('helvetica', 'bold');
      pdf.doc.text('ACCEPTED', pdf.margin, startY);
      startY += 10;
      
      const headers = ['Status', 'Patient', 'Reference #', 'Clinic', 'Service', 'Date & Time'];
      const rows = acceptedItems.map(apt => [
        getStatusLabel(apt.status),
        apt.patientName || 'N/A',
        apt.reference || 'N/A',
        apt.clinicName || 'N/A',
        apt.serviceName || 'N/A',
        `${apt.date || 'N/A'} ${apt.time || 'N/A'}`
      ]);

      pdf.autoTable({
        head: [headers],
        body: rows,
        startY: startY,
        margin: { top: pdf.contentStartY, bottom: pdf.footerHeight + 10, left: pdf.margin, right: pdf.margin }
      });
      startY = pdf.doc.lastAutoTable.finalY + 20;
    }

    // Add cancelling items table if any
    if (cancellingItems.length > 0) {
      pdf.doc.setFontSize(14);
      pdf.doc.setFont('helvetica', 'bold');
      pdf.doc.text('USER REQUESTING CANCELLATION', pdf.margin, startY);
      startY += 10;
      
      const headers = ['Status', 'Patient', 'Reference #', 'Clinic', 'Service', 'Date & Time'];
      const rows = cancellingItems.map(apt => [
        getStatusLabel(apt.status),
        apt.patientName || 'N/A',
        apt.reference || 'N/A',
        apt.clinicName || 'N/A',
        apt.serviceName || 'N/A',
        `${apt.date || 'N/A'} ${apt.time || 'N/A'}`
      ]);

      pdf.autoTable({
        head: [headers],
        body: rows,
        startY: startY,
        margin: { top: pdf.contentStartY, bottom: pdf.footerHeight + 10, left: pdf.margin, right: pdf.margin }
      });
    }

    pdf.save(`upcoming-appointments-report-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const exportToExcel = async () => {
    const workbook = XLSX.utils.book_new();

    const summaryData = [
      ['UPCOMING APPOINTMENTS REPORT'],
      ['Generated', new Date().toLocaleString()],
      ['Status Filter', statusFilter || 'All Statuses'],
      [],
      ['APPOINTMENTS SUMMARY'],
      ['Total Appointments', filteredAppointments.length],
      ['Confirmed', filteredAppointments.filter(item => item.status === 'CONFIRMED').length],
      ['Accepted', filteredAppointments.filter(item => item.status === 'ACCEPTED').length],
      ['User Cancelling', filteredAppointments.filter(item => item.status === 'CANCELLING').length],
    ];
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

    // Separate data by status
    const confirmedItems = filteredAppointments.filter(item => item.status === 'CONFIRMED');
    const acceptedItems = filteredAppointments.filter(item => item.status === 'ACCEPTED');
    const cancellingItems = filteredAppointments.filter(item => item.status === 'CANCELLING');

    const contentData = [
      ['APPOINTMENT DETAILS'],
    ];

    // Add confirmed items
    if (confirmedItems.length > 0) {
      contentData.push(['CONFIRMED']);
      contentData.push(['Status', 'Patient', 'Reference #', 'Clinic', 'Service', 'Date & Time']);
      confirmedItems.forEach(apt => {
        contentData.push([
          getStatusLabel(apt.status),
          apt.patientName || 'N/A',
          apt.reference || 'N/A',
          apt.clinicName || 'N/A',
          apt.serviceName || 'N/A',
          `${apt.date || 'N/A'} ${apt.time || 'N/A'}`
        ]);
      });
    }

    // Add accepted items
    if (acceptedItems.length > 0) {
      contentData.push([]);
      contentData.push(['ACCEPTED']);
      contentData.push(['Status', 'Patient', 'Reference #', 'Clinic', 'Service', 'Date & Time']);
      acceptedItems.forEach(apt => {
        contentData.push([
          getStatusLabel(apt.status),
          apt.patientName || 'N/A',
          apt.reference || 'N/A',
          apt.clinicName || 'N/A',
          apt.serviceName || 'N/A',
          `${apt.date || 'N/A'} ${apt.time || 'N/A'}`
        ]);
      });
    }

    // Add cancelling items
    if (cancellingItems.length > 0) {
      contentData.push([]);
      contentData.push(['USER REQUESTING CANCELLATION']);
      contentData.push(['Status', 'Patient', 'Reference #', 'Clinic', 'Service', 'Date & Time']);
      cancellingItems.forEach(apt => {
        contentData.push([
          getStatusLabel(apt.status),
          apt.patientName || 'N/A',
          apt.reference || 'N/A',
          apt.clinicName || 'N/A',
          apt.serviceName || 'N/A',
          `${apt.date || 'N/A'} ${apt.time || 'N/A'}`
        ]);
      });
    }

    const contentSheet = XLSX.utils.aoa_to_sheet(contentData);
    XLSX.utils.book_append_sheet(workbook, contentSheet, 'Appointments');

    XLSX.writeFile(workbook, `upcoming-appointments-report-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportToCSV = async () => {
    let csvData = [
      ['UPCOMING APPOINTMENTS REPORT'],
      ['Generated', new Date().toLocaleString()],
      ['Status Filter', statusFilter || 'All Statuses'],
      [],
      ['APPOINTMENTS SUMMARY'],
      ['Total Appointments', filteredAppointments.length],
      ['Confirmed', filteredAppointments.filter(item => item.status === 'CONFIRMED').length],
      ['Accepted', filteredAppointments.filter(item => item.status === 'ACCEPTED').length],
      ['User Cancelling', filteredAppointments.filter(item => item.status === 'CANCELLING').length],
      [],
      ['APPOINTMENT DETAILS'],
    ];

    // Separate data by status
    const confirmedItems = filteredAppointments.filter(item => item.status === 'CONFIRMED');
    const acceptedItems = filteredAppointments.filter(item => item.status === 'ACCEPTED');
    const cancellingItems = filteredAppointments.filter(item => item.status === 'CANCELLING');

    // Add confirmed items
    if (confirmedItems.length > 0) {
      csvData.push(['CONFIRMED']);
      csvData.push(['Status', 'Patient', 'Reference #', 'Clinic', 'Service', 'Date & Time']);
      confirmedItems.forEach(apt => {
        csvData.push([
          getStatusLabel(apt.status),
          apt.patientName || 'N/A',
          apt.reference || 'N/A',
          apt.clinicName || 'N/A',
          apt.serviceName || 'N/A',
          `${apt.date || 'N/A'} ${apt.time || 'N/A'}`
        ]);
      });
    }

    // Add accepted items
    if (acceptedItems.length > 0) {
      csvData.push([]);
      csvData.push(['ACCEPTED']);
      csvData.push(['Status', 'Patient', 'Reference #', 'Clinic', 'Service', 'Date & Time']);
      acceptedItems.forEach(apt => {
        csvData.push([
          getStatusLabel(apt.status),
          apt.patientName || 'N/A',
          apt.reference || 'N/A',
          apt.clinicName || 'N/A',
          apt.serviceName || 'N/A',
          `${apt.date || 'N/A'} ${apt.time || 'N/A'}`
        ]);
      });
    }

    // Add cancelling items
    if (cancellingItems.length > 0) {
      csvData.push([]);
      csvData.push(['USER REQUESTING CANCELLATION']);
      csvData.push(['Status', 'Patient', 'Reference #', 'Clinic', 'Service', 'Date & Time']);
      cancellingItems.forEach(apt => {
        csvData.push([
          getStatusLabel(apt.status),
          apt.patientName || 'N/A',
          apt.reference || 'N/A',
          apt.clinicName || 'N/A',
          apt.serviceName || 'N/A',
          `${apt.date || 'N/A'} ${apt.time || 'N/A'}`
        ]);
      });
    }

    const csvContent = csvData.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `upcoming-appointments-report-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const handleExport = async (format) => {
    try {
      setIsExporting(true);
      setSelectedExportFormat(format);
      
      if (format === 'pdf') {
        await exportToPDF();
      } else if (format === 'excel') {
        await exportToExcel();
      } else if (format === 'csv') {
        await exportToCSV();
      }
    } catch (error) {
      console.error('Export error:', error);
      setBannerMessage('Export failed');
      setBannerType('error');
    } finally {
      setIsExporting(false);
      setSelectedExportFormat(null);
      setIsExportModalOpen(false);
    }
  };

  const attachmentRootId = viewing?.isFollowUp === true
    ? viewing.originalAppointmentId
    : viewing?.id;

  // Card component for the viewer modal
  const AppointmentRecordCard = ({ record, isFollowUp, isCurrentViewing, indented }) => {
    const notes = record.displayNotes || [];
    const attachments = record.attachments || [];
    return (
      <div style={{ display: 'flex', gap: '0' }}>
        {indented && (
          <div style={{ width: '24px', flexShrink: 0 }} />
        )}
        <div
          className={`ma-attachment-record ${isFollowUp ? 'is-follow-up' : 'is-original'}`}
          style={{
            flex: 1,
            marginBottom: indented ? '10px' : '0',
            border: isCurrentViewing ? '1.5px solid #3b82f6' : undefined,
            borderRadius: '10px',
            overflow: 'hidden'
          }}
        >
          <div className="ma-attachment-record-header">
            <div className="ma-attachment-record-title">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                {isFollowUp ? (
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '3px 8px',
                    borderRadius: '5px',
                    background: '#dbeafe',
                    color: '#1e40af',
                    fontSize: '11px',
                    fontWeight: '600',
                    letterSpacing: '0.02em'
                  }}>
                    ↩ Follow-up Appointment
                  </span>
                ) : (
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '3px 8px',
                    borderRadius: '5px',
                    background: '#dcfce7',
                    color: '#15803d',
                    fontSize: '11px',
                    fontWeight: '600',
                    letterSpacing: '0.02em'
                  }}>
                    Original Appointment
                  </span>
                )}
                {isCurrentViewing && (
                  <span style={{
                    padding: '3px 8px',
                    borderRadius: '5px',
                    background: '#eff6ff',
                    color: '#3b82f6',
                    fontSize: '11px',
                    fontWeight: '600',
                    border: '1px solid #bfdbfe'
                  }}>
                    Current
                  </span>
                )}
              </div>
              <h4 style={{ margin: '6px 0 4px' }}>{record.serviceName || 'Appointment'}</h4>
              <span className="ma-attachment-record-datetime">
                <FiCalendar size={13} />
                {record.date || 'Date unavailable'}{record.time ? ` • ${record.time}` : ''}
              </span>
            </div>
            <div className="ma-attachment-reference">
              <span>Reference #</span>
              <strong>{record.reference || 'N/A'}</strong>
            </div>
          </div>
          {notes.length > 0 && (
            <div className="ma-attachment-record-section">
              <h5><FiFileText size={14} /> Findings <span>{notes.length}</span></h5>
              {notes.map((note, index) => (
                <div key={note.id || index} className="ma-attachment-finding">
                  <p>{note.text}</p>
                  <div>
                    By {note.proName || note.createdBy || 'Medical Professional'}
                    {note.createdAt && <> • {new Date(note.createdAt).toLocaleDateString()}</>}
                  </div>
                </div>
              ))}
            </div>
          )}
          {attachments.length > 0 && (
            <div className="ma-attachment-record-section">
              <h5><FiPaperclip size={14} /> Attachments <span>{attachments.length}</span></h5>
              {attachments.map((file, index) => (
                <div key={file.fileUrl || index} className="ma-attachment-file">
                  <span className="ma-attachment-file-icon"><FiPaperclip size={16} /></span>
                  <div className="ma-attachment-file-details">
                    <a href={file.fileUrl} target="_blank" rel="noopener noreferrer">
                      {file.fileName || 'Unnamed File'}
                    </a>
                    <div>
                      By {file.uploadedBy || 'Medical Professional'}
                      {file.uploadedAt && <> • {new Date(file.uploadedAt).toLocaleDateString()}</>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {notes.length === 0 && attachments.length === 0 && (
            <p className="ma-attachment-record-empty">
              No findings or attachments for this appointment.
            </p>
          )}
        </div>
      </div>
    );
  };

  // Build a structured list: all appointments grouped as chains (original → follow-ups),
  // sorted chronologically. Each original is followed immediately by its follow-ups.
  const buildChainedRecords = (records) => {
    // Separate originals (not a follow-up) and follow-ups
    const originals = records.filter(r => !r.isFollowUp);
    const followUps = records.filter(r => r.isFollowUp === true);

    // Sort originals by date ascending
    const parseDate = (r) => {
      try { return new Date(r.createdAt || r.date || 0).getTime(); } catch { return 0; }
    };
    originals.sort((a, b) => parseDate(a) - parseDate(b));

    const chained = [];
    originals.forEach(orig => {
      chained.push(orig);
      // Attach follow-ups for this original, sorted by date
      const children = followUps
        .filter(f => f.originalAppointmentId === orig.id)
        .sort((a, b) => parseDate(a) - parseDate(b));
      children.forEach(child => chained.push(child));
    });

    // Any follow-ups whose parent is not in records (edge case) — append at end
    const chainedIds = new Set(chained.map(r => r.id));
    followUps.filter(f => !chainedIds.has(f.id)).forEach(f => chained.push(f));

    return chained;
  };

  const attachmentRecordsToDisplay = buildChainedRecords(patientRecords);

  return (
    <div className="ma-main review-appointments-panel">
      <ExportModal 
        isOpen={isExportModalOpen} 
        onClose={() => setIsExportModalOpen(false)} 
        onExport={handleExport} 
        isExporting={isExporting} 
      />

      {/* Upcoming Appointments Header */}
      <div className="content-library-header-custom" style={{
        background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
        borderRadius: '20px',
        padding: '32px',
        marginBottom: '24px',
        boxShadow: '0 8px 32px rgba(59, 130, 246, 0.3)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Medical Professional - Left Aligned */}
        <div style={{
          position: 'absolute',
          left: '32px',
          top: '50%',
          transform: 'translateY(-50%)'
        }}>
          <div style={{
            padding: '16px 32px',
            borderRadius: '50px',
            background: 'rgba(147, 197, 253, 0.2)',
            border: '1px solid rgba(147, 197, 253, 0.3)'
          }}>
            <h2 style={{
              margin: 0,
              fontSize: '28px',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              color: 'transparent',
              letterSpacing: '0.02em',
              textShadow: 'none'
            }}>
              Medical Professional
            </h2>
          </div>
        </div>

        {/* Title and Subtitle */}
        <div style={{ textAlign: 'right' }}>
          <h2 style={{
            margin: 0,
            fontSize: '36px',
            fontWeight: '800',
            color: '#ffffff !important',
            background: 'none !important',
            WebkitBackgroundClip: 'unset !important',
            WebkitTextFillColor: '#ffffff !important',
            backgroundClip: 'unset !important',
            letterSpacing: '-0.02em',
            textShadow: '0 2px 8px rgba(0,0,0,0.2)'
          }}>
            Upcoming Appointments
          </h2>
          <p style={{
            margin: '12px 0 0 0',
            fontSize: '18px',
            color: '#ffffff',
            fontWeight: '500',
            textShadow: '0 1px 4px rgba(0,0,0,0.15)'
          }}>
            View and manage your scheduled upcoming appointments
          </p>
        </div>

        {/* CSS Animations */}
        <style>{`
          .content-library-header-custom h2 {
            color: #ffffff !important;
            background: none !important;
            -webkit-background-clip: unset !important;
            -webkit-text-fill-color: #ffffff !important;
            background-clip: unset !important;
          }
          .content-library-header-custom p {
            color: #ffffff !important;
          }
        `}</style>
      </div>
      
      <Banner
        message={bannerMessage}
        type={bannerType}
        onClose={() => setBannerMessage("")}
      />

      <NotificationModal
        message={notificationMessage}
        type={notificationType}
        onClose={() => setNotificationMessage("")}
      />

      {/* Custom Tooltip for Action Buttons */}
      {tooltip.visible && (
        <div
          className={`admin-action-tooltip ${tooltip.type}`}
          style={{
            left: tooltip.x,
            top: tooltip.y
          }}
        >
          {tooltip.text}
        </div>
      )}
      {/* ATTACHMENTS AND FINDINGS VIEWER */}
      {viewing && showAttachmentsViewerModal && createPortal(
        <div className="ma-modal-backdrop">
          <div className="ma-modal-card wide ma-attachment-viewer" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <button
              className="ma-modal-close"
              type="button"
              onClick={() => {
                setShowAttachmentsViewerModal(false);
                setViewing(null);
                setPatientRecords([]);
              }}
              aria-label="Close attachments and findings"
            >
              <FiX />
            </button>
            <div className="ma-attachment-viewer-header">
              <div className="ma-attachment-viewer-icon"><FiPaperclip size={19} /></div>
              <div>
                <h3>Attachments & Findings</h3>
                <p>Appointment records for <strong>{viewing.patientName}</strong></p>
              </div>
            </div>

            <div className="ma-attachment-viewer-scroll" style={{ overflowY: 'auto', flex: 1, paddingRight: '12px' }}>
              {isLoadingRecords ? (
                <div className="ma-attachment-viewer-empty" style={{ textAlign: 'center', padding: '40px 20px', color: '#6b7280' }}>
                  Loading attachments and findings...
                </div>
              ) : attachmentRecordsToDisplay.some(record => record.displayNotes?.length > 0 || record.attachments?.length > 0) ? (
                <div className="ma-attachment-record-list" style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                  {(() => {
                    // Render chains: for each original, render it then its follow-ups indented
                    const rendered = [];
                    let i = 0;
                    while (i < attachmentRecordsToDisplay.length) {
                      const record = attachmentRecordsToDisplay[i];
                      const isFollowUp = record.isFollowUp === true;

                      if (!isFollowUp) {
                        // Collect consecutive follow-ups that belong to this original
                        const children = [];
                        let j = i + 1;
                        while (
                          j < attachmentRecordsToDisplay.length &&
                          attachmentRecordsToDisplay[j].isFollowUp === true &&
                          attachmentRecordsToDisplay[j].originalAppointmentId === record.id
                        ) {
                          children.push(attachmentRecordsToDisplay[j]);
                          j++;
                        }

                        const hasChain = children.length > 0;

                        rendered.push(
                          <div key={record.id} style={{ marginBottom: '18px' }}>
                            {/* Original appointment card */}
                            <AppointmentRecordCard
                              record={record}
                              isFollowUp={false}
                              isCurrentViewing={record.id === viewing?.id || record.id === attachmentRootId}
                              indented={false}
                              showConnector={hasChain}
                            />
                            {/* Follow-up cards indented below */}
                            {children.map((child, idx) => (
                              <div key={child.id} style={{ marginTop: '10px' }}>
                                <AppointmentRecordCard
                                  record={child}
                                  isFollowUp={true}
                                  isCurrentViewing={child.id === viewing?.id}
                                  indented={true}
                                  showConnector={idx < children.length - 1}
                                />
                              </div>
                            ))}
                          </div>
                        );
                        i = j;
                      } else {
                        // Orphan follow-up (parent not in list) — render as-is
                        rendered.push(
                          <div key={record.id} style={{ marginBottom: '18px' }}>
                            <AppointmentRecordCard
                              record={record}
                              isFollowUp={true}
                              isCurrentViewing={record.id === viewing?.id}
                              indented={false}
                              showConnector={false}
                            />
                          </div>
                        );
                        i++;
                      }
                    }
                    return rendered;
                  })()}
                </div>
              ) : (
                <div className="ma-attachment-viewer-empty">
                  <FiAlertCircle size={32} />
                  <p>No attachments or findings available for this patient.</p>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Top Row: Search, Date From, Date To, Service, Refresh */}
      <div className="admin-aura-top-controls" style={{ marginTop: 20 }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
          <FiSearch style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} />
          <input
            type="text"
            placeholder="Search Appointment"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              handleFilterChange();
            }}
            className="admin-aura-search"
            style={{ paddingLeft: 36, width: '100%' }}
          />
        </div>

        <div style={{ position: 'relative', flex: '0 0 140px' }}>
          <FiCalendar style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} />
          <input
            type="date"
            className="admin-aura-search"
            style={{ paddingLeft: 36, width: '100%' }}
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              handleFilterChange();
            }}
          />
        </div>
        
        <div style={{ fontSize: '14px', color: '#6b7280', fontWeight: '500', flexShrink: 0 }}>to</div>
        
        <div style={{ position: 'relative', flex: '0 0 140px' }}>
          <FiCalendar style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} />
          <input
            type="date"
            className="admin-aura-search"
            style={{ paddingLeft: 36, width: '100%' }}
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              handleFilterChange();
            }}
          />
        </div>

        <select
          className="admin-aura-select"
          value={serviceFilter}
          onChange={(e) => {
            setServiceFilter(e.target.value);
            handleFilterChange();
          }}
          style={{ flex: '0 0 180px' }}
        >
          <option value="">All Services</option>
          {SERVICE_FILTER_OPTIONS.map((service) => (
            <option key={service.value} value={service.value}>{service.label}</option>
          ))}
        </select>

        <button 
          onClick={handleRefreshAppointments}
          disabled={isRefreshing}
          className="admin-primary-gradient-btn"
          style={{ opacity: isRefreshing ? 0.6 : 1, cursor: isRefreshing ? 'not-allowed' : 'pointer' }}
        >
          <FiRefreshCw className={isRefreshing ? "spinning" : ""} />
          {isRefreshing ? "Refreshing..." : "Refresh"}
        </button>
        <button
          onClick={() => setIsExportModalOpen(true)}
          className="admin-primary-gradient-btn"
        >
          <FiFileText /> Export Report
        </button>
      </div>

      {/* Second Row: Status Buttons + Export to PDF + Pagination */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <div className="floating-index-tabs ma-status-tabs" aria-label="Filter appointments by status" style={{ display: "flex", gap: "8px" }}>
          {STATUS_FILTER_TABS.map((tab) => (
            <button
              key={tab.value || "all"}
              type="button"
              className={`floating-tab ${statusFilter === tab.value ? "active floating-tab-active" : ""}`}
              onClick={() => {
                setStatusFilter(tab.value);
                handleFilterChange();
              }}
            >
              {tab.label}
              <span className="tab-count">{getStatusTabCount(tab.value)}</span>
            </button>
          ))}
        </div>

        <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
            <div style={{ fontSize: "14px", color: "#6b7280" }}>
              {filteredAppointments.length > 0 ? (
                `${startIndex + 1}-${Math.min(endIndex, filteredAppointments.length)} of ${filteredAppointments.length}`
              ) : (
                '0 of 0'
              )}
            </div>

            {/* Pagination Arrows */}
            <div style={{ display: "flex", gap: "8px" }}>
              <button 
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                style={{ 
                  backgroundColor: '#1e3a8a', 
                  color: 'white', 
                  border: 'none', 
                  padding: '8px 12px', 
                  borderRadius: '4px', 
                  cursor: 'pointer',
                  width: '32px'
                }}
              >
                ←
              </button>
              
              <button 
                onClick={handleNextPage}
                disabled={currentPage === totalPages || totalPages === 0}
                style={{ 
                  backgroundColor: '#1e3a8a', 
                  color: 'white', 
                  border: 'none', 
                  padding: '8px 12px', 
                  borderRadius: '4px', 
                  cursor: 'pointer',
                  width: '32px'
                }}
              >
                →
              </button>
            </div>
        </div>
      </div>

      <div className="ma-table-wrap">
        <table className="ma-table">
          <thead>
            <tr style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' }}>
              <th {...getSortHeaderProps("upcomingStatus", "Appointment Status")} style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none', cursor: 'pointer' }}>STATUS {getAppointmentSortIndicator(sortConfig, "upcomingStatus")}</th>
              <th {...getSortHeaderProps("patientName", "Patient Name")} style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none', cursor: 'pointer' }}>PATIENT {getAppointmentSortIndicator(sortConfig, "patientName")}</th>
              <th {...getSortHeaderProps("reference", "Reference Number")} style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none', cursor: 'pointer' }}>REFERENCE # {getAppointmentSortIndicator(sortConfig, "reference")}</th>
              <th {...getSortHeaderProps("clinicName", "Clinic")} style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none', cursor: 'pointer' }}>CLINIC {getAppointmentSortIndicator(sortConfig, "clinicName")}</th>
              <th {...getSortHeaderProps("serviceName", "Service")} style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none', cursor: 'pointer' }}>SERVICE {getAppointmentSortIndicator(sortConfig, "serviceName")}</th>
              <th {...getSortHeaderProps("dateTime", "Appointment Date & Time")} style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none', cursor: 'pointer' }}>DATE & TIME {getAppointmentSortIndicator(sortConfig, "dateTime")}</th>
              <th style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none' }}>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <>
                {[...Array(5)].map((_, i) => (
                  <tr key={i} className="admin-aura-skeleton-row">
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '40%' }} /></td>
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: `${60 + (i % 3) * 15}%` }} /></td>
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '50%' }} /></td>
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: `${55 + (i % 4) * 10}%` }} /></td>
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '65%' }} /></td>
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '55%' }} /></td>
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-actions" /></td>
                  </tr>
                ))}
              </>
            ) : paginatedAppointments.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", padding: "40px 20px", color: "#6b7280" }}>
                  No appointments found
                </td>
              </tr>
            ) : (
              paginatedAppointments.map((apt) => (
              <tr key={apt.id}>
                <td>
                  <span className={`ma-badge ma-badge-${apt.status}`}>
                    {getStatusLabel(apt.status)}
                  </span>
                </td>
                <td>
                  <div>{apt.patientName}</div>
                  {apt.reasonForAssistance?.length > 0 && (
                    <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {(apt.reasonForAssistance || []).map((reason, idx) => (
                        <span
                          key={idx}
                          style={{
                            fontSize: '10px',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontWeight: '500',
                            textTransform: 'capitalize',
                            backgroundColor: getAssistanceBadgeColor(reason),
                            color: getAssistanceBadgeTextColor(reason)
                          }}
                        >
                          {reason}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td>
                  <div>{apt.reference}</div>
                  {apt.isFollowUp && (
                    <span style={{
                      display: 'inline-block',
                      marginTop: '4px',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      background: '#dbeafe',
                      color: '#1e40af',
                      fontSize: '10px',
                      fontWeight: '600'
                    }}>
                      Follow Up Appointment
                    </span>
                  )}
                </td>
                <td>{apt.clinicName}</td>
                <td>{apt.serviceName}</td>
                <td>
                  <div>{apt.date}</div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>{apt.time}</div>
                </td>
                <td>
                  <div className="ma-row-actions">
                    {apt.status === "CONFIRMED" && (
                      <>
                        <button
                          type="button"
                          className="ma-action-btn approve"
                          onClick={() => onAccept && onAccept(apt)}
                          style={{ background: '#ecfdf5', color: '#059669', borderColor: '#10b981' }}
                          onMouseEnter={(e) => { e.target.style.background = '#d1fae5'; handleActionMouseEnter(e, "Accept Appointment", 'approve'); }}
                          onMouseMove={handleActionMouseMove}
                          onMouseLeave={(e) => { e.target.style.background = '#ecfdf5'; handleActionMouseLeave(); }}
                        >
                          <FiCheckCircle size={16} />
                        </button>
                        <button
                          type="button"
                          className="ma-action-btn reschedule"
                          onClick={() => setRescheduleTarget(apt)}
                          style={{ background: '#fef3c7', color: '#d97706', borderColor: '#f59e0b' }}
                          onMouseEnter={(e) => { e.target.style.background = '#fde68a'; handleActionMouseEnter(e, "Reschedule Appointment", 'info'); }}
                          onMouseMove={handleActionMouseMove}
                          onMouseLeave={(e) => { e.target.style.background = '#fef3c7'; handleActionMouseLeave(); }}
                        >
                          <FiClock size={16} />
                        </button>
                        <button
                          type="button"
                          className="ma-action-btn cancel"
                          onClick={() => setCancelTarget(apt)}
                          style={{ background: '#fee2e2', color: '#dc2626', borderColor: '#ef4444' }}
                          onMouseEnter={(e) => { e.target.style.background = '#fecaca'; handleActionMouseEnter(e, "Cancel Appointment", 'delete'); }}
                          onMouseMove={handleActionMouseMove}
                          onMouseLeave={(e) => { e.target.style.background = '#fee2e2'; handleActionMouseLeave(); }}
                        >
                          <FiX size={16} />
                        </button>
                      </>
                    )}
                    {apt.status === "ACCEPTED" && (
                      <>
                        <button
                          type="button"
                          className="ma-action-btn view"
                          onClick={() => {
                            setViewing(apt);
                            setShowViewDataModal(true);
                          }}
                          style={{ background: '#dbeafe', color: '#1d4ed8', borderColor: '#3b82f6' }}
                          onMouseEnter={(e) => { e.target.style.background = '#bfdbfe'; handleActionMouseEnter(e, "View Data", 'view'); }}
                          onMouseMove={handleActionMouseMove}
                          onMouseLeave={(e) => { e.target.style.background = '#dbeafe'; handleActionMouseLeave(); }}
                        >
                          <FiEye size={16} />
                        </button>
                        <button
                          type="button"
                          className="ma-action-btn complete"
                          onClick={() => onComplete && onComplete(apt)}
                          style={{ background: '#ecfdf5', color: '#059669', borderColor: '#10b981' }}
                          onMouseEnter={(e) => { e.target.style.background = '#d1fae5'; handleActionMouseEnter(e, "Complete Appointment", 'approve'); }}
                          onMouseMove={handleActionMouseMove}
                          onMouseLeave={(e) => { e.target.style.background = '#ecfdf5'; handleActionMouseLeave(); }}
                        >
                          <FiCheckSquare size={16} />
                        </button>
                        <button
                          type="button"
                          className="ma-action-btn reschedule"
                          onClick={() => {
                            setViewing(apt);
                            setShowAttachmentsModal(true);
                          }}
                          style={{ background: '#fef3c7', color: '#d97706', borderColor: '#f59e0b' }}
                          onMouseEnter={(e) => { e.target.style.background = '#fde68a'; handleActionMouseEnter(e, "Attachments", 'reschedule'); }}
                          onMouseMove={handleActionMouseMove}
                          onMouseLeave={(e) => { e.target.style.background = '#fef3c7'; handleActionMouseLeave(); }}
                        >
                          <FiPaperclip size={16} />
                        </button>
                        <button
                          type="button"
                          className="ma-action-btn cancel"
                          onClick={() => setCancelTarget(apt)}
                          style={{ background: '#fee2e2', color: '#dc2626', borderColor: '#ef4444' }}
                          onMouseEnter={(e) => { e.target.style.background = '#fecaca'; handleActionMouseEnter(e, "Cancel Appointment", 'delete'); }}
                          onMouseMove={handleActionMouseMove}
                          onMouseLeave={(e) => { e.target.style.background = '#fee2e2'; handleActionMouseLeave(); }}
                        >
                          <FiX size={16} />
                        </button>
                      </>
                    )}
                    {apt.status === "CANCELLING" && (
                      <button
                        type="button"
                        className="ma-action-btn cancel"
                        onClick={() => setCancelTarget(apt)}
                        style={{ background: '#fee2e2', color: '#dc2626', borderColor: '#ef4444' }}
                        onMouseEnter={(e) => { e.target.style.background = '#fecaca'; handleActionMouseEnter(e, "Cancel Appointment", 'delete'); }}
                        onMouseMove={handleActionMouseMove}
                        onMouseLeave={(e) => { e.target.style.background = '#fee2e2'; handleActionMouseLeave(); }}
                      >
                        <FiX size={16} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* VIEW DATA MODAL */}
      {showViewDataModal && viewing && createPortal(
        <div className="ma-modal-backdrop">
          <div className="ma-modal-card" style={{ maxWidth: '600px' }}>
            <button
              className="ma-modal-close"
              type="button"
              onClick={() => {
                setShowViewDataModal(false);
                setViewing(null);
              }}
            >
              ✕
            </button>
            <h3>Patient Records - {viewing.patientName}</h3>
            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '20px' }}>
              Viewing approved clinical notes and appointment details.
            </p>

            <div className="ma-view-data-content" style={{ maxHeight: '600px', overflowY: 'auto', marginBottom: '20px', paddingRight: '12px', paddingBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', padding: '12px', background: '#f0fdf4', borderRadius: '12px', border: '1px solid #dcfce7' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#166534', fontSize: '14px', fontWeight: '600' }}>
                  <FiShield size={18} />
                  <span>Secure Access Active</span>
                </div>
              </div>

              {/* Patient Medical History Sections */}
              {patientInfo && (
                <>
                  {renderInfoSection("Health Information", patientInfo.healthInformation)}
                  {renderInfoSection("Medication History", patientInfo.medicationHistory)}
                  {renderInfoSection("Sexual Health History", patientInfo.sexualHistory)}
                  {renderInfoSection("Patient Information", patientInfo.patientInformation)}
                  {patientInfo.guardianInformation && renderInfoSection("Guardian / Accompanist Information", patientInfo.guardianInformation)}
                </>
              )}

              <div className="ma-patient-summary" style={{ marginBottom: '24px' }}>
                <h4 style={{ fontSize: '14px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px', fontWeight: '700' }}>Current Appointment</h4>
                <div className="ma-modal-details" style={{ background: '#fff', padding: '16px', borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                  <div className="ma-detail-row">
                    <span className="ma-detail-label">Service</span>
                    <span className="ma-detail-value" style={{ color: '#111827', fontWeight: '600' }}>{viewing.serviceName}</span>
                  </div>
                  <div className="ma-detail-row">
                    <span className="ma-detail-label">Date & Time</span>
                    <span className="ma-detail-value">{viewing.date} • {viewing.time}</span>
                  </div>
                  <div className="ma-detail-row">
                    <span className="ma-detail-label">Reference</span>
                    <span className="ma-detail-value" style={{ fontFamily: 'monospace', color: '#2563eb' }}>{viewing.reference}</span>
                  </div>
                </div>
              </div>

              {false && (<>
              <h4 style={{ fontSize: '14px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '16px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FiPaperclip size={18} color="#2563eb" />
                Attachments & Findings
              </h4>

              {patientRecords.some(record => record.displayNotes.length > 0 || record.attachments.length > 0) ? (
                <div className="ma-attachments-findings-section" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {patientRecords.map((record) => {
                    const notes = record.displayNotes || [];
                    const attachments = record.attachments || [];

                    if (notes.length === 0 && attachments.length === 0) return null;

                    return (
                      <div key={record.id} style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0' }}>
                        <h5 style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                          {record.serviceName} - {record.date} • {record.time}
                        </h5>

                        {notes.length > 0 && (
                          <div style={{ marginBottom: attachments.length > 0 ? '16px' : 0 }}>
                            <h6 style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '8px' }}>Findings</h6>
                            {notes.map((note, index) => (
                              <div key={note.id || index} style={{ padding: '12px', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '8px' }}>
                                <p style={{ margin: 0, fontSize: '13px', color: '#1e293b', whiteSpace: 'pre-wrap' }}>{note.text}</p>
                                <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '8px' }}>
                                  <span style={{ fontWeight: '600' }}>By {note.proName || note.createdBy || 'Medical Professional'}</span>
                                  {note.createdAt && <> • {new Date(note.createdAt).toLocaleDateString()} • {new Date(note.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</>}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {attachments.length > 0 && (
                          <div>
                            <h6 style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '8px' }}>Uploads</h6>
                            {attachments.map((file, index) => (
                              <div key={file.fileUrl || index} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '8px' }}>
                                <FiPaperclip size={16} color="#2563eb" />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <a href={file.fileUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', fontWeight: '600', color: '#2563eb', textDecoration: 'none', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {file.fileName || 'Unnamed File'}
                                  </a>
                                  <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                                    <span style={{ fontWeight: '600' }}>By {file.uploadedBy || 'Medical Professional'}</span>
                                    {file.uploadedAt && <> • {new Date(file.uploadedAt).toLocaleDateString()}</>}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
              <div className="ma-attachments-findings-section" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {/* Findings Section */}
                <div className="ma-findings-sub">
                  <h5 style={{ fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FiFileText size={16} color="#4b5563" />
                    Consultation Findings
                  </h5>
                  {viewing?.attachmentNotes?.length > 0 ? (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px', maxHeight: '280px', overflowY: 'auto', paddingRight: '8px' }}>
                      {viewing.attachmentNotes.map((note, idx) => (
                        <div key={idx} style={{ 
                          padding: '16px', 
                          borderRadius: '12px', 
                          border: '1px solid #e2e8f0', 
                          background: '#ffffff', 
                          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                          position: 'relative',
                          overflow: 'hidden'
                        }}>
                          <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', background: '#3b82f6' }}></div>
                          <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#1e293b', whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>{note.text}</p>
                          <div style={{ 
                            paddingTop: '12px', 
                            borderTop: '1px solid #f1f5f9', 
                            fontSize: '11px', 
                            color: '#64748b', 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center' 
                          }}>
                            <span style={{ fontWeight: '600', color: '#475569', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <FiShield size={12} /> By {note.createdBy}
                            </span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <FiClock size={12} /> {new Date(note.createdAt).toLocaleDateString()} • {new Date(note.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '24px', 
                      background: '#f8fafc', 
                      borderRadius: '12px', 
                      border: '1px dashed #cbd5e1',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <FiFileText size={24} color="#94a3b8" />
                      <p style={{ margin: 0, fontSize: '13px', color: '#64748b', fontStyle: 'italic' }}>No findings recorded for this visit.</p>
                    </div>
                  )}
                </div>

                {/* Attachments Section */}
                <div className="ma-attachments-sub">
                  <h5 style={{ fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FiPaperclip size={16} color="#4b5563" />
                    Uploaded Documents
                  </h5>
                  {viewing?.attachments?.length > 0 ? (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px', maxHeight: '280px', overflowY: 'auto', paddingRight: '8px' }}>
                      {viewing.attachments.map((file, index) => (
                        <div key={index} style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '14px', 
                          padding: '14px', 
                          borderRadius: '12px', 
                          border: '1px solid #e2e8f0', 
                          background: '#fff', 
                          boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                          transition: 'all 0.2s ease'
                        }}>
                          <div style={{ 
                            width: '40px', 
                            height: '40px', 
                            borderRadius: '10px', 
                            background: '#f1f5f9', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            flexShrink: 0,
                            color: '#2563eb'
                          }}>
                            {file.fileType?.includes('image') ? (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                            ) : file.fileType?.includes('pdf') ? (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                            ) : (
                              <FiPaperclip size={20} />
                            )}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <a 
                              href={file.fileUrl} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              style={{ 
                                display: 'block', 
                                fontSize: '14px', 
                                fontWeight: '600', 
                                color: '#2563eb', 
                                textDecoration: 'none', 
                                overflow: 'hidden', 
                                textOverflow: 'ellipsis', 
                                whiteSpace: 'nowrap',
                                marginBottom: '2px'
                              }}
                            >
                              {file.fileName || 'Unnamed File'}
                            </a>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '500' }}>
                                {file.fileType?.split('/')[1]?.toUpperCase() || 'FILE'} • {new Date(file.uploadedAt).toLocaleDateString()}
                              </span>
                              <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                                By {file.uploadedBy}
                              </span>
                            </div>
                          </div>
                          <a 
                            href={file.fileUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{
                              padding: '8px',
                              borderRadius: '8px',
                              background: '#f8fafc',
                              color: '#64748b',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: '1px solid #e2e8f0'
                            }}
                            title="Open File"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                          </a>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '24px', 
                      background: '#f8fafc', 
                      borderRadius: '12px', 
                      border: '1px dashed #cbd5e1',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <FiPaperclip size={24} color="#94a3b8" />
                      <p style={{ margin: 0, fontSize: '13px', color: '#64748b', fontStyle: 'italic' }}>No files uploaded for this visit.</p>
                    </div>
                  )}
                </div>

                {/* Global Empty State */}
                {(!viewing?.attachmentNotes?.length && !viewing?.attachments?.length) && (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '32px 20px', 
                    background: '#f1f5f9', 
                    borderRadius: '16px', 
                    border: '1px solid #e2e8f0' 
                  }}>
                    <div style={{ 
                      width: '48px', 
                      height: '48px', 
                      borderRadius: '50%', 
                      background: '#fff', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      margin: '0 auto 12px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                    }}>
                      <FiAlertCircle size={24} color="#94a3b8" />
                    </div>
                    <p style={{ margin: 0, fontSize: '14px', color: '#475569', fontWeight: '500' }}>
                      No attachments or findings available for this appointment.
                    </p>
                  </div>
                )}
              </div>
              )}
              </>)}
            </div>

            <div className="ma-modal-footer">
              <button
                className="ma-primary-btn"
                onClick={() => {
                  setShowViewDataModal(false);
                  setShowAttachmentsViewerModal(true);
                }}
                style={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  color: '#ffffff',
                  border: 'none'
                }}
              >
                View Attachments
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ATTACHMENTS MODAL */}
      {viewing && showAttachmentsModal && createPortal(
        <div className="ma-modal-backdrop">
          <div className="ma-modal-card wide" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <button
              className="ma-modal-close"
              type="button"
              onClick={() => {
                setShowAttachmentsModal(false);
                setViewing(null);
                setClinicalNote("");
                setAttachmentFiles([]);
                setExistingNotes([]);
                setRemovedAttachmentIds([]);
                setSelectedNoteId("");
                setShowAttachmentEditor(false);
                setNoteError("");
                setNdaAgreed(false);
              }}
            >
              ✕
            </button>
            <h3>Appointment Attachments & Clinical Notes</h3>

            {(() => {
              // Only show expiry banner for completed appointments
              if (viewing?.status === 'COMPLETED') {
                const attachmentExpiry = getAttachmentExpiry(viewing);
                const isExpired = attachmentExpiry ? attachmentExpiry <= new Date() : false;
                return (
                  <div style={{
                    margin: '0 0 20px',
                    padding: '12px 16px',
                    borderRadius: '10px',
                    border: `1px solid ${isExpired ? '#fecaca' : '#bfdbfe'}`,
                    background: isExpired ? '#fef2f2' : '#eff6ff',
                    color: isExpired ? '#991b1b' : '#1e40af',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    fontSize: '13px'
                  }}>
                    <FiClock size={18} />
                    <span>
                      <strong>Attachment availability:</strong>{' '}
                      {attachmentExpiry
                        ? `${isExpired ? 'Expired on' : 'Available until'} ${attachmentExpiry.toLocaleString()}`
                        : 'Expiry date unavailable'}
                    </span>
                  </div>
                );
              }
              return null;
            })()}

            <div style={{ overflowY: 'auto', flex: 1, minHeight: 0, paddingRight: '4px' }}>

            <div className="ma-modal-details" style={{ marginBottom: '24px' }}>
              <div className="ma-detail-row">
                <span className="ma-detail-label">Patient</span>
                <span className="ma-detail-value">{viewing?.patientName}</span>
              </div>
              <div className="ma-detail-row">
                <span className="ma-detail-label">Reference #</span>
                <span className="ma-detail-value">{viewing?.reference}</span>
              </div>
            </div>

            {/* Clinical Notes / Findings - Only show when NOT in editing mode */}
            {!showAttachmentEditor && (
              <div className="ma-form-field">
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>Clinical Notes / Findings *</label>
                <textarea
                  className="ma-textarea"
                  placeholder="Enter clinical findings, consultation summaries, laboratory interpretations, prescribed recommendations, follow-up instructions, or relevant patient care notes."
                  value={clinicalNote}
                  onChange={(e) => setClinicalNote(e.target.value)}
                  style={{ height: '120px', borderRadius: '8px', border: '1px solid #e5e7eb', padding: '12px', width: '100%', resize: 'none' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                  <span style={{ fontSize: '11px', color: noteError ? '#dc2626' : '#6b7280' }}>
                    {noteError || "Min 10, Max 500 characters"}
                  </span>
                  <span style={{ fontSize: '11px', color: clinicalNote.length > 500 ? '#dc2626' : '#6b7280' }}>
                    {clinicalNote.length}/500
                  </span>
                </div>
              </div>
            )}

            {/* Add Attachments section - Only show when NOT in editing mode */}
            {!showAttachmentEditor && (
              <div className="ma-form-field" style={{ marginTop: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>Add Attachments</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <input 
                    type="file" 
                    id="clinical-attachment" 
                    style={{ display: 'none' }} 
                    multiple // Feature 4: Support multiple uploads
                    onChange={(e) => {
                      if (e.target.files) {
                        const newFiles = Array.from(e.target.files);
                        setAttachmentFiles(prev => [...prev, ...newFiles]);
                      }
                    }}
                    accept=".pdf,.png,.jpg,.jpeg"
                  />
                  <label 
                    htmlFor="clinical-attachment" 
                    style={{ 
                      cursor: 'pointer', 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px', 
                      padding: '10px 16px', 
                      borderRadius: '8px', 
                      background: '#f3f4f6', 
                      border: '1px solid #e5e7eb',
                      transition: 'all 0.2s'
                    }}
                    className="attachment-upload-label"
                  >
                    <img src="https://www.svgrepo.com/show/467995/attachment-right-2.svg" alt="attach" style={{ width: '20px', height: '20px' }} />
                    <span style={{ fontSize: '14px', fontWeight: '500' }}>Select Files</span>
                  </label>
                </div>

                {/* List of files pending upload */}
                {attachmentFiles.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {attachmentFiles.map((file, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 12px', background: '#ecfdf5', borderRadius: '16px', border: '1px solid #10b981' }}>
                        <FiFileText size={14} color="#059669" />
                        <span style={{ fontSize: '12px', color: '#065f46', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
                        <button onClick={() => setAttachmentFiles(prev => prev.filter((_, i) => i !== idx))} style={{ border: 'none', background: 'none', color: '#059669', cursor: 'pointer', display: 'flex' }}><FiX size={14} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <p style={{ fontSize: '11px', color: '#6b7280', marginTop: '6px' }}>Supported: PDF, PNG, JPG, JPEG (Multiple files allowed)</p>

              {/* NDA Agreement Checkbox */}
              <div style={{ marginTop: '12px', padding: '12px 14px', borderRadius: '8px', background: '#f8fafc', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <input
                  type="checkbox"
                  id="nda-agree"
                  checked={ndaAgreed}
                  onChange={(e) => setNdaAgreed(e.target.checked)}
                  style={{ marginTop: '2px', flexShrink: 0, accentColor: '#2563eb', width: '15px', height: '15px', cursor: 'pointer' }}
                />
                <label htmlFor="nda-agree" style={{ fontSize: '12px', color: '#374151', lineHeight: '1.5', cursor: 'pointer' }}>
                  I have read and agree to comply with the{' '}
                  <button
                    type="button"
                    onClick={() => window.open('/AuraVit_Medical_Professional_Confidentiality_NDA.pdf', '_blank')}
                    style={{ background: 'none', border: 'none', padding: 0, color: '#2563eb', fontSize: '12px', fontWeight: '600', cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    Medical Professional Confidentiality and Non-Disclosure Agreement
                  </button>
                  .
                </label>
              </div>
            </div>
            )}

            <div style={{ marginTop: '32px', paddingTop: '20px', borderTop: '1px solid #f3f4f6' }}>
              {/* Edit Section - Above the row */}
              {isAppointmentEditable(viewing) && showAttachmentEditor ? (
                <div style={{ padding: '16px', borderRadius: '10px', background: '#f8fafc', border: '1px solid #e5e7eb', marginBottom: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#374151', margin: 0 }}>Edit Consultation Summary & Files</h4>
                    <button type="button" onClick={() => setShowAttachmentEditor(false)} style={{ border: '1px solid #d1d5db', background: 'transparent', color: '#64748b', cursor: 'pointer', fontSize: '12px', fontWeight: '600', padding: '4px 12px', borderRadius: '4px' }}>Cancel</button>
                  </div>
                  {existingNotes.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', fontWeight: '600', color: '#4b5563' }}>
                        Finding to edit
                        <select
                          value={selectedNoteId}
                          onChange={(e) => {
                            const nextId = e.target.value;
                            setSelectedNoteId(nextId);
                            setClinicalNote(existingNotes.find((note) => note.id === nextId)?.text || "");
                          }}
                          style={{ display: 'block', width: '100%', marginTop: '6px', padding: '9px', border: '1px solid #d1d5db', borderRadius: '8px', background: '#fff' }}
                        >
                          <option value="">— select a finding —</option>
                          {existingNotes.map((note, index) => (
                            <option key={note.id || index} value={note.id}>
                              {index === existingNotes.length - 1 ? "Latest finding" : `Finding ${index + 1}`} — {new Date(note.createdAt).toLocaleString()}
                            </option>
                          ))}
                        </select>
                      </label>
                      {selectedNoteId && (
                        <button
                          type="button"
                          onClick={() => setShowDeleteConfirm(true)}
                          disabled={isUploading}
                          style={{
                            border: '1px solid #fecaca',
                            background: '#fef2f2',
                            color: '#dc2626',
                            cursor: 'pointer',
                            borderRadius: '4px',
                            padding: '6px 12px',
                            fontSize: '12px',
                            fontWeight: '600'
                          }}
                        >
                          Delete This Finding
                        </button>
                      )}

                      {/* Delete Confirmation Modal */}
                      {showDeleteConfirm && createPortal(
                        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
                          <div
                            className="modal-content"
                            style={{ maxWidth: '400px', padding: '24px', textAlign: 'center' }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#374151', marginBottom: '12px' }}>Delete Finding?</h3>
                            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '20px' }}>
                              Are you sure you want to delete this finding? This action cannot be undone.
                            </p>
                            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                              <button
                                type="button"
                                onClick={() => setShowDeleteConfirm(false)}
                                style={{
                                  padding: '10px 20px',
                                  borderRadius: '6px',
                                  border: '1px solid #d1d5db',
                                  background: 'white',
                                  color: '#374151',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  fontWeight: '600'
                                }}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                onClick={async () => {
                                  if (!selectedNoteId) return;

                                  try {
                                    setIsUploading(true);

                                    // Find the note to remove
                                    const noteToRemove = existingNotes.find(note => note.id === selectedNoteId);
                                    if (!noteToRemove) {
                                      throw new Error("Finding not found");
                                    }

                                    // Delete from Firestore using arrayRemove
                                    await updateDoc(doc(db, "appointments", viewing.id), {
                                      attachmentNotes: arrayRemove(noteToRemove)
                                    });

                                    setNotificationMessage("Finding deleted successfully!");
                                    setNotificationType("success");
                                    setSelectedNoteId("");
                                    setClinicalNote("");
                                    setShowDeleteConfirm(false);

                                    // Refresh data
                                    const apptDoc = await getDoc(doc(db, "appointments", viewing.id));
                                    if (apptDoc.exists()) {
                                      const data = apptDoc.data();
                                      appointmentDataCache.current[viewing.id] = data;
                                      lastFetchedAppointmentId.current = viewing.id;
                                      setExistingNotes(data.attachmentNotes || []);
                                      setViewing(prev => ({
                                        ...prev,
                                        attachments: data.attachments || [],
                                        attachmentNotes: data.attachmentNotes || []
                                      }));
                                    }
                                  } catch (err) {
                                    console.error("Error deleting finding:", err);
                                    setNoteError(err.message || "Failed to delete finding. Please try again.");
                                  } finally {
                                    setIsUploading(false);
                                  }
                                }}
                                disabled={isUploading}
                                style={{
                                  padding: '10px 20px',
                                  borderRadius: '6px',
                                  border: 'none',
                                  background: '#dc2626',
                                  color: 'white',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  fontWeight: '600'
                                }}
                              >
                                {isUploading ? "Deleting..." : "Delete"}
                              </button>
                            </div>
                          </div>
                        </div>,
                        document.body
                      )}
                    </div>
                  )}
                  <textarea
                    className="ma-textarea"
                    placeholder="Enter consultation findings or summary (minimum 10 characters)."
                    value={clinicalNote}
                    onChange={(e) => setClinicalNote(e.target.value)}
                    style={{ height: '100px', borderRadius: '8px', border: '1px solid #e5e7eb', padding: '12px', width: '100%', resize: 'none' }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginTop: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <input
                        type="file"
                        id="upcoming-clinical-attachment"
                        style={{ display: 'none' }}
                        multiple
                        accept=".pdf,.png,.jpg,.jpeg"
                        onChange={(e) => setAttachmentFiles(e.target.files ? Array.from(e.target.files) : [])}
                      />
                      <label htmlFor="upcoming-clinical-attachment" className="attachment-upload-label" style={{ cursor: 'pointer', padding: '9px 14px', borderRadius: '8px', background: '#f3f4f6', border: '1px solid #e5e7eb' }}>
                        <FiPaperclip size={16} /> Select Files
                      </label>
                    </div>
                    <button className="ma-primary-btn" onClick={handleAttachmentUpload} disabled={isUploading || (!clinicalNote.trim() && attachmentFiles.length === 0 && removedAttachmentIds.length === 0)}>
                      {isUploading ? "Saving..." : "Save Changes"}
                    </button>
                  </div>

                  {/* Existing Uploaded Files with Remove Buttons */}
                  {viewing?.attachments?.length > 0 && (
                    <div style={{ marginTop: '16px', padding: '12px', borderRadius: '8px', background: '#fff', border: '1px solid #e5e7eb' }}>
                      <h5 style={{ fontSize: '13px', fontWeight: '600', color: '#374151', margin: '0 0 10px 0' }}>Existing Uploaded Files</h5>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '200px', overflowY: 'auto' }}>
                        {viewing.attachments.filter((file) => !removedAttachmentIds.includes(file.id)).map((file, index) => (
                          <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px', borderRadius: '6px', border: '1px solid #e5e7eb', background: '#f9fafb' }}>
                            <FiPaperclip color="#6b7280" size={14} />
                            <div style={{ flex: 1, overflow: 'hidden' }}>
                              <a href={file.fileUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#2563eb', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {file.fileName || 'Unnamed File'}
                              </a>
                              <span style={{ fontSize: '10px', color: '#9ca3af' }}>{new Date(file.uploadedAt).toLocaleDateString()} by {file.uploadedBy}</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => setRemovedAttachmentIds(prev => [...new Set([...prev, file.id])])}
                              title={`Remove ${file.fileName || 'document'}`}
                              onMouseEnter={(e) => { e.currentTarget.style.background = '#fee2e2'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                              style={{ border: 'none', background: 'transparent', color: '#dc2626', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '5px', borderRadius: '4px' }}
                              aria-label={`Remove ${file.fileName || 'document'}`}
                            >
                              <FiX size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <p style={{ margin: '12px 0 0', fontSize: '11px', color: '#6b7280' }}>
                    Remove existing documents above or add new files before saving.
                  </p>
                  <div style={{ marginTop: '8px', fontSize: '11px', color: noteError ? '#dc2626' : '#6b7280' }}>
                    {noteError || `${attachmentFiles.length} file(s) selected to add • ${removedAttachmentIds.length} file(s) marked for removal`}
                  </div>
                </div>
              ) : !isAppointmentEditable(viewing) ? (
                <div style={{ marginBottom: '20px', padding: '12px 16px', borderRadius: '10px', background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', fontSize: '13px' }}>
                  Editing is disabled because the record is finalized on the blockchain.
                </div>
              ) : isAppointmentEditable(viewing) && (existingNotes.length > 0 || (viewing?.attachments?.length > 0)) && (
                <div style={{ marginBottom: '20px', textAlign: 'right' }}>
                  <button
                    type="button"
                    onClick={() => setShowAttachmentEditor(true)}
                    style={{
                      border: '1px solid #bfdbfe',
                      background: '#eff6ff',
                      color: '#1d4ed8',
                      cursor: 'pointer',
                      borderRadius: '6px',
                      padding: '8px 16px',
                      fontSize: '13px',
                      fontWeight: '600'
                    }}
                  >
                    Edit Consultation Summary & Files
                  </button>
                </div>
              )}

              {/* Only show the two-column layout when NOT in editing mode */}
              {!showAttachmentEditor && (
                <div style={{ display: 'flex', gap: '24px' }}>
                {/* Left: Existing Notes */}
                <div style={{ flex: '0 0 50%', minWidth: 0 }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#374151', marginBottom: '12px' }}>Clinical Findings History</h4>
                  {existingNotes.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '250px', overflowY: 'auto', paddingRight: '8px' }}>
                      {existingNotes.map((note, idx) => (
                        <div key={idx} style={{ padding: '12px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#f9fafb' }}>
                          <p style={{ margin: 0, fontSize: '13px', color: '#374151', whiteSpace: 'pre-wrap' }}>{note.text}</p>
                          <div style={{ marginTop: '6px', fontSize: '10px', color: '#9ca3af', display: 'flex', justifyContent: 'space-between' }}>
                            <span>By {note.createdBy}</span>
                            <span>{new Date(note.createdAt).toLocaleDateString()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ fontSize: '13px', color: '#9ca3af', fontStyle: 'italic' }}>No findings recorded yet.</p>
                  )}
                </div>

                {/* Right: Existing Attachments */}
                <div style={{ flex: '0 0 calc(50% - 24px)', minWidth: 0 }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#374151', marginBottom: '12px' }}>Uploaded Files</h4>
                  {viewing?.attachments?.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '250px', overflowY: 'auto', paddingRight: '8px' }}>
                      {viewing.attachments.filter((file) => !removedAttachmentIds.includes(file.id)).map((file, index) => (
                        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff' }}>
                          <FiPaperclip color="#6b7280" />
                          <div style={{ flex: 1, overflow: 'hidden' }}>
                            <a href={file.fileUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#2563eb', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {file.fileName || 'Unnamed File'}
                            </a>
                            <span style={{ fontSize: '10px', color: '#9ca3af' }}>{new Date(file.uploadedAt).toLocaleDateString()} by {file.uploadedBy}</span>
                          </div>
                          {showAttachmentEditor && (
                            <button
                              type="button"
                              onClick={() => setRemovedAttachmentIds(prev => [...new Set([...prev, file.id])])}
                              title={`Remove ${file.fileName || 'document'}`}
                              onMouseEnter={(e) => { e.currentTarget.style.background = '#fee2e2'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                              style={{ border: 'none', background: 'transparent', color: '#dc2626', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '7px', borderRadius: '6px' }}
                              aria-label={`Remove ${file.fileName || 'document'}`}
                            >
                              <FiX size={16} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ fontSize: '13px', color: '#9ca3af', fontStyle: 'italic' }}>No files found.</p>
                  )}
                </div>
                </div>
              )}

            </div>

            {/* Footer - Only show when NOT in editing mode */}
            {!showAttachmentEditor && (
              <div className="ma-modal-footer" style={{ marginTop: '32px', flexShrink: 0 }}>
                <button
                  className="ma-primary-btn"
                  onClick={handleAttachmentUpload}
                  disabled={isUploading || !ndaAgreed || (!clinicalNote.trim() && attachmentFiles.length === 0)}
                >
                  {isUploading ? "Saving..." : "Save Findings & Upload"}
                </button>
              </div>
            )}
          </div>
          </div>
        </div>,
        document.body
      )}

      {/* VIEW MODAL / ACCESS FLOW (Deprecated for ACCEPTED status, but kept for others) */}
      {viewing && !showAttachmentsModal && !showAttachmentsViewerModal && !showViewDataModal && accessState !== "viewingData" && createPortal(
        <div className="ma-modal-backdrop">
          <div className="ma-modal-card">
            <button
              className="ma-modal-close"
              type="button"
              onClick={() => {
                setViewing(null);
                resetAccessFlow();
              }}
            >
              ✕
            </button>

            <h3>Upcoming Appointment</h3>

            <div className="ma-modal-details">
              <div className="ma-detail-row">
                <span className="ma-detail-label">Patient</span>
                <span className="ma-detail-value">{viewing.patientName}</span>
              </div>
              <div className="ma-detail-row">
                <span className="ma-detail-label">Reference #</span>
                <span className="ma-detail-value">{viewing.reference}</span>
              </div>
              <div className="ma-detail-row">
                <span className="ma-detail-label">Clinic</span>
                <span className="ma-detail-value">{viewing.clinicName}</span>
              </div>
              <div className="ma-detail-row">
                <span className="ma-detail-label">Service</span>
                <span className="ma-detail-value">{viewing.serviceName}</span>
              </div>
              <div className="ma-detail-row">
                <span className="ma-detail-label">Date &amp; Time</span>
                <span className="ma-detail-value">
                  {viewing.date} • {viewing.time}
                </span>
              </div>
              <div className="ma-detail-row">
                <span className="ma-detail-label">Status</span>
                <span className="ma-detail-value">
                  <span className={`ma-badge ma-badge-${viewing.status}`}>
                    {getStatusLabel(viewing.status)}
                  </span>
                </span>
              </div>
            </div>

            <div className="ma-modal-footer">
              <button
                type="button"
                className={
                  accessState === "approved"
                    ? "ma-primary-btn ma-primary-btn-approved"
                    : "ma-primary-btn"
                }
                onClick={() => {
                  if (accessState === "approved") {
                    handleOpenAccessedData();
                  } else if (accessState === "idle") {
                    setShowAccessRequestModal(true);
                  }
                }}
                disabled={accessState === "requesting"}
              >
                {accessState === "idle" && "Request Access"}
                {accessState === "requesting" && "Requesting..."}
                {accessState === "approved" && "Approved Access!"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ACCESS REQUEST MODAL */}
      {viewing && showAccessRequestModal && createPortal(
        <div className="ma-modal-backdrop slim">
          <div className="ma-modal-card narrow">
            <button
              className="ma-modal-close"
              type="button"
              onClick={() => {
                setShowAccessRequestModal(false);
              }}
            >
              ✕
            </button>
            <h3>Request Data Access</h3>
            <p className="ma-helper-text">
              Specify how long you need access and what information you will
              review.
            </p>

            <form onSubmit={handleSendAccessRequest}>
              <div className="ma-form-field">
                <label>Access Duration (Hours)</label>
                <select
                  className="ma-input"
                  value={accessDuration}
                  onChange={(e) => setAccessDuration(e.target.value)}
                  required
                >
                  <option value="">Select Duration</option>
                  <option value="1">1 Hour</option>
                  <option value="4">4 Hours</option>
                  <option value="12">12 Hours</option>
                  <option value="24">24 Hours</option>
                </select>
              </div>

              <div className="ma-form-field">
                <label>Staff ID</label>
                <input
                  type="text"
                  className="ma-input"
                  placeholder="Enter Staff ID"
                  value={staffId}
                  onChange={(e) => setStaffId(e.target.value)}
                  required
                />
              </div>

              <div className="ma-form-field">
                <label>Staff Password</label>
                <input
                  type="password"
                  className="ma-input"
                  placeholder="Enter Staff Password"
                  value={staffPassword}
                  onChange={(e) => setStaffPassword(e.target.value)}
                  required
                />
              </div>

              <div className="ma-modal-footer">
                <button
                  type="button"
                  className="ma-secondary-btn"
                  onClick={() => setShowAccessRequestModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="ma-primary-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Sending..." : "Send Request"}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}



      {/* RESCHEDULE MODAL */}
      {rescheduleTarget && createPortal(
        <div className="ma-modal-backdrop">
          <div className="ma-modal-card walkin-modal" style={{ maxWidth: '850px', width: '95%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header" style={{ padding: '24px 32px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#111827' }}>Reschedule Appointment</h3>
              
            </div>
            
            <div className="modal-body-scroll" style={{ overflowY: 'auto', padding: '32px', flex: 1, backgroundColor: '#fff', minHeight: 0 }}>
              <p style={{ marginBottom: '24px', color: '#4b5563' }}>Select a new date and time for <strong>{rescheduleTarget.patientName}</strong>'s appointment.</p>
              
              <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px' }}>
                {/* Left Column: Current Appointment Info + Reason */}
                <div className="walkin-left-col" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  <div className="form-group">
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#4b5563' }}>Current Date</label>
                    <input type="text" value={rescheduleTarget.date} readOnly style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: '#f9fafb', color: '#6b7280', fontSize: '15px', cursor: 'not-allowed' }} />
                  </div>
                  <div className="form-group">
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#4b5563' }}>Current Time</label>
                    <input type="text" value={rescheduleTarget.time} readOnly style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: '#f9fafb', color: '#6b7280', fontSize: '15px', cursor: 'not-allowed' }} />
                  </div>
                  <div className="form-group">
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#4b5563' }}>Service</label>
                    <input type="text" value={rescheduleTarget.serviceName} readOnly style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: '#f9fafb', color: '#6b7280', fontSize: '15px', cursor: 'not-allowed' }} />
                    <div style={{ marginTop: '12px', display: 'flex', gap: '20px', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '8px', height: '8px', backgroundColor: '#19458c', borderRadius: '50%' }} />
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>Scheduled</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '8px', height: '8px', backgroundColor: '#fdebb2', borderRadius: '50%' }} />
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>Walk-in</span>
                      </div>
                    </div>
                  </div>

                  {/* Reschedule Reason Field */}
                  <div className="form-group" style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#4b5563' }}>Reschedule Reason *</label>
                    <select 
                      value={rescheduleReason}
                      onChange={(e) => {
                        setRescheduleReason(e.target.value);
                        setRescheduleReasonError("");
                      }}
                      style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: '#fff', color: '#374151', fontSize: '15px', cursor: 'pointer' }}
                    >
                      <option value="schedule_conflict">Schedule conflict with another confirmed appointment</option>
                      <option value="staff_unavailable">Medical staff temporarily unavailable</option>
                      <option value="schedule_passed">Selected appointment schedule has already passed</option>
                      <option value="clinic_adjustment">Clinic operational adjustment</option>
                      <option value="other">Other</option>
                    </select>
                    
                    {rescheduleReason === "other" && (
                      <textarea
                        value={rescheduleReasonOther}
                        onChange={(e) => {
                          setRescheduleReasonOther(e.target.value);
                          setRescheduleReasonError("");
                        }}
                        placeholder="Please specify the reason (10-500 characters)"
                        style={{
                          width: '100%',
                          marginTop: '12px',
                          padding: '12px 16px',
                          borderRadius: '8px',
                          border: rescheduleReasonError ? '1px solid #ef4444' : '1px solid #e5e7eb',
                          backgroundColor: '#fff',
                          color: '#1f2937',
                          fontSize: '14px',
                          minHeight: '100px',
                          fontFamily: 'inherit',
                          resize: 'vertical'
                        }}
                      />
                    )}
                    
                    {rescheduleReasonError && (
                      <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#ef4444' }}>{rescheduleReasonError}</p>
                    )}
                  </div>
                </div>

                {/* Right Column: New Date & Time Selection */}
                <div className="walkin-right-col">
                  <div className="datetime-picker-card" style={{ background: '#f8fafc', padding: '24px', borderRadius: '12px', border: '1px solid #f1f5f9' }}>
                    <div className="calendar-widget">
                      <div className="calendar-nav" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <button type="button" onClick={() => {
                          const d = new Date(selectedRescheduleDate);
                          d.setMonth(d.getMonth() - 1);
                          setSelectedRescheduleDate(d);
                        }} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', width: '32px', height: '32px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>
                        <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#1e293b', letterSpacing: '0.025em' }}>{selectedRescheduleDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }).toUpperCase()}</h4>
                        <button type="button" onClick={() => {
                          const d = new Date(selectedRescheduleDate);
                          d.setMonth(d.getMonth() + 1);
                          setSelectedRescheduleDate(d);
                        }} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', width: '32px', height: '32px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>
                      </div>

                      <div className="calendar-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', textAlign: 'center' }}>
                        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(day => (
                          <div key={day} style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', padding: '8px 0' }}>{day}</div>
                        ))}
                        {getCalendarDays(selectedRescheduleDate).map((day, i) => {
                          if (!day) return <div key={`empty-${i}`} />;
                          const d = new Date(selectedRescheduleDate.getFullYear(), selectedRescheduleDate.getMonth(), day);
                          const isSelected = formatDateToPH(d) === formatDateToPH(selectedRescheduleDate);
                          const isToday = formatDateToPH(d) === formatDateToPH(new Date());
                          const isPast = formatDateToPH(d) < formatDateToPH(new Date());
                          const isWeekendDate = isWeekend(d);
                          const isDisabled = isPast || isWeekendDate;

                          return (
                            <button
                              key={day}
                              type="button"
                              disabled={isDisabled}
                              title={isWeekendDate ? "Clinic closed on weekends." : ""}
                              onClick={() => {
                                if (isWeekendDate) {
                                  setBannerMessage("The clinic is closed on weekends. Please select a weekday for rescheduling.");
                                  setBannerType("error");
                                  return;
                                }
                                if (!isPast) setSelectedRescheduleDate(d);
                              }}
                              style={{
                                padding: '8px 0',
                                borderRadius: '6px',
                                border: 'none',
                                background: isSelected ? '#3b82f6' : 'transparent',
                                color: isSelected ? '#fff' : (isDisabled ? '#cbd5e1' : '#475569'),
                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                fontSize: '13px',
                                fontWeight: isSelected || isToday ? 600 : 400,
                                position: 'relative'
                              }}
                            >
                              {day}
                              {isToday && !isSelected && <span style={{ position: 'absolute', bottom: '4px', left: '50%', transform: 'translateX(-50%)', width: '4px', height: '4px', background: '#3b82f6', borderRadius: '50%' }} />}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="time-slots-container" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <div>
                          <span style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Morning</span>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginTop: '8px' }}>
                            {timeSlots.morning.map(time => {
                              const { valid, reason } = isTimeSlotValid(time);
                              // Find ALL booked slots for this time and service
                              const matchingSlots = bookedSlots.filter(slot => {
                                const sameService = slot.serviceId === rescheduleTarget.serviceId || slot.serviceName === rescheduleTarget.serviceName;
                                const sameTime = slot.time === time;
                                return sameService && sameTime;
                              });
                              // Get unique booking types to avoid duplicate dots
                              const uniqueBookingTypes = [...new Set(matchingSlots.map(slot => slot.bookingType))];
                              
                              return (
                                <button
                                  key={time}
                                  type="button"
                                  disabled={!valid}
                                  onClick={() => setSelectedRescheduleTime(time)}
                                  style={{
                                    padding: '8px 2px 16px 2px',
                                    borderRadius: '6px',
                                    border: '1px solid',
                                    borderColor: selectedRescheduleTime === time ? '#29499d' : (valid ? '#e2e8f0' : '#f1f5f9'),
                                    background: selectedRescheduleTime === time ? '#29499d' : (valid ? '#fff' : '#f8fafc'),
                                    color: selectedRescheduleTime === time ? '#fff' : (valid ? '#475569' : '#9ca3af'),
                                    fontSize: '11px',
                                    fontWeight: selectedRescheduleTime === time ? 700 : 500,
                                    cursor: valid ? 'pointer' : 'not-allowed',
                                    position: 'relative'
                                  }}
                                >
                                  {time}
                                  {uniqueBookingTypes.length > 0 && (
                                    <div style={{ 
                                      position: 'absolute', 
                                      bottom: '4px', 
                                      left: '50%', 
                                      transform: 'translateX(-50%)', 
                                      display: 'flex', 
                                      gap: '4px',
                                      alignItems: 'center'
                                    }}>
                                      {uniqueBookingTypes.map((bookingType, idx) => (
                                        <div 
                                          key={idx}
                                          style={{ 
                                            width: '8px', 
                                            height: '8px', 
                                            backgroundColor: bookingType === "WALK_IN" ? '#fdebb2' : '#19458c', 
                                            borderRadius: '50%' 
                                          }} 
                                        />
                                      ))}
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        
                        <div>
                          <span style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Afternoon</span>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginTop: '8px' }}>
                            {timeSlots.afternoon.map(time => {
                              const { valid, reason } = isTimeSlotValid(time);
                              // Find ALL booked slots for this time and service
                              const matchingSlots = bookedSlots.filter(slot => {
                                const sameService = slot.serviceId === rescheduleTarget.serviceId || slot.serviceName === rescheduleTarget.serviceName;
                                const sameTime = slot.time === time;
                                return sameService && sameTime;
                              });
                              // Get unique booking types to avoid duplicate dots
                              const uniqueBookingTypes = [...new Set(matchingSlots.map(slot => slot.bookingType))];
                              
                              return (
                                <button
                                  key={time}
                                  type="button"
                                  disabled={!valid}
                                  onClick={() => setSelectedRescheduleTime(time)}
                                  style={{
                                    padding: '8px 2px 16px 2px',
                                    borderRadius: '6px',
                                    border: '1px solid',
                                    borderColor: selectedRescheduleTime === time ? '#29499d' : (valid ? '#e2e8f0' : '#f1f5f9'),
                                    background: selectedRescheduleTime === time ? '#29499d' : (valid ? '#fff' : '#f8fafc'),
                                    color: selectedRescheduleTime === time ? '#fff' : (valid ? '#475569' : '#9ca3af'),
                                    fontSize: '11px',
                                    fontWeight: selectedRescheduleTime === time ? 700 : 500,
                                    cursor: valid ? 'pointer' : 'not-allowed',
                                    position: 'relative'
                                  }}
                                >
                                  {time}
                                  {uniqueBookingTypes.length > 0 && (
                                    <div style={{ 
                                      position: 'absolute', 
                                      bottom: '4px', 
                                      left: '50%', 
                                      transform: 'translateX(-50%)', 
                                      display: 'flex', 
                                      gap: '4px',
                                      alignItems: 'center'
                                    }}>
                                      {uniqueBookingTypes.map((bookingType, idx) => (
                                        <div 
                                          key={idx}
                                          style={{ 
                                            width: '8px', 
                                            height: '8px', 
                                            backgroundColor: bookingType === "WALK_IN" ? '#fdebb2' : '#19458c', 
                                            borderRadius: '50%' 
                                          }} 
                                        />
                                      ))}
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer" style={{ padding: '20px 32px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'flex-end', gap: '12px', flexShrink: 0 }}>
              <button className="ma-outline-btn" onClick={() => setRescheduleTarget(null)}>Cancel</button>
              <button 
                className="ma-primary-btn" 
                onClick={handleReschedule} 
                disabled={!selectedRescheduleTime || isSubmittingReschedule}
                style={{ minWidth: '140px' }}
              >
                {isSubmittingReschedule ? 'Rescheduling...' : 'Reschedule'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* CANCEL MODAL */}
      <CancelAppointmentModal 
        isOpen={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        onConfirm={handleCancel}
        appointment={cancelTarget}
        theme="staff"
        isSubmitting={isCancelling}
      />
    </div>
  );
};

export default UpcomingAppointments;
